// src/pages/shop/ShopDashboard.js - COMPLETE ENHANCED VERSION

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Store,
  Package,
  Users,
  Settings,
  ChevronRight,
  BarChart2,
  Edit3,
  Eye,
  TrendingUp,
  Activity,
  DollarSign,
  ShoppingBag,
  MapPin,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  Film,
  Image as ImageIcon,
  ArrowUp,
  ArrowDown,
  Search,
  Filter,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Pause,
  Play,
  Trash2,
  Send,
  CreditCard,
  FileText,
  TrendingDown,
  Award,
  MessageCircle,
  LogIn,
  ExternalLink,
  AlertTriangle,
  XCircle,
  Bell,
  X,
  Menu
} from 'lucide-react';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  setDoc,
  arrayUnion,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { calculateOutstandingBalance, getNextMilestone, SHOP_OPENING_FEE } from '../../utils/billingTiers';
import { resetStripeMode } from '../../utils/stripe';
import { formatShopPrice } from '../../utils/currency';

// Module-level cache — survives re-renders and navigation, cleared only on manual refresh
let _dashboardCache = null;

// Styled Components - Mobile-First Approach
const DashboardContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #0B0B3B, #1A1A4C);
  color: #FFFFFF;
  padding-bottom: 2rem;

  @media (max-width: 768px) {
    padding-bottom: 1rem;
  }
`;

const DashboardHeader = styled.header`
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(128, 0, 0, 0.3);
  position: sticky;
  top: 0;
  z-index: 100;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const HeaderContent = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  background: linear-gradient(45deg, #800000, #4A0404);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    text-align: center;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    
    button {
      flex: 1;
    }
  }
`;

const ActionButton = styled.button`
  background: ${props => props.primary ? 'linear-gradient(45deg, #800000, #4A0404)' : 'transparent'};
  color: white;
  border: ${props => props.primary ? 'none' : '1px solid rgba(128, 0, 0, 0.5)'};
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(128, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;

    &:hover {
      transform: none;
    }

    &:active {
      transform: scale(0.98);
    }
  }
`;

const DashboardContent = styled.main`
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid rgba(128, 0, 0, 0.2);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(128, 0, 0, 0.5);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    gap: 0.25rem;
    margin-bottom: 1rem;
  }
`;

const Tab = styled.button`
  background: ${props => props.active ? 'rgba(128, 0, 0, 0.2)' : 'transparent'};
  color: ${props => props.active ? '#800000' : 'rgba(255, 255, 255, 0.7)'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#800000' : 'transparent'};
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;

  &:hover {
    color: #800000;
    background: rgba(128, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    gap: 0.4rem;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const StatCard = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(128, 0, 0, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.trend === 'up' ? '#4CAF50' : props.trend === 'down' ? '#FF4444' : '#800000'};
  }

  .stat-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
  }

  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${props => props.iconBg || 'rgba(128, 0, 0, 0.2)'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.iconColor || '#800000'};
  }

  .stat-value {
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
    background: linear-gradient(45deg, #800000, #4A0404);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .stat-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  .stat-change {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: ${props => props.trend === 'up' ? '#4CAF50' : props.trend === 'down' ? '#FF4444' : 'rgba(255, 255, 255, 0.5)'};
    font-size: 0.85rem;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 1.25rem;

    .stat-icon {
      width: 40px;
      height: 40px;

      svg {
        width: 20px;
        height: 20px;
      }
    }

    .stat-value {
      font-size: 1.75rem;
    }

    .stat-label {
      font-size: 0.85rem;
    }

    .stat-change {
      font-size: 0.8rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1rem;

    .stat-value {
      font-size: 1.5rem;
    }
  }
`;

const DataTable = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  border: 1px solid rgba(128, 0, 0, 0.3);
  overflow: hidden;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    border-radius: 8px;
    margin-bottom: 1.5rem;
  }
`;

const TableHeader = styled.div`
  background: rgba(128, 0, 0, 0.2);
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(128, 0, 0, 0.3);
  flex-wrap: wrap;
  gap: 1rem;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    color: #800000;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    
    h3 {
      font-size: 1rem;
      width: 100%;
    }
  }

  @media (max-width: 480px) {
    padding: 0.75rem;

    h3 {
      font-size: 0.9rem;
    }
  }
`;

const TableControls = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    gap: 0.5rem;
  }
`;

const SearchInput = styled.input`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(128, 0, 0, 0.3);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  color: white;
  font-size: 0.9rem;
  min-width: 200px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #800000;
  }

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
    width: 100%;
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
  }
`;

const FilterSelect = styled.select`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(128, 0, 0, 0.3);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #800000;
  }

  option {
    background: #1A1A4C;
  }

  @media (max-width: 768px) {
    flex: 1;
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
  }
`;

// Mobile-friendly table wrapper
const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    /* Show scroll hint */
    &::after {
      content: '→ Scroll for more';
      position: sticky;
      right: 0;
      background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.8) 20%);
      padding: 0.5rem 1rem;
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.6);
      display: ${props => props.$hasScroll ? 'block' : 'none'};
      text-align: right;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px; /* Ensure table doesn't compress too much */

  th, td {
    text-align: left;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(128, 0, 0, 0.2);
  }

  th {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: rgba(0, 0, 0, 0.2);
    white-space: nowrap;
  }

  td {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba(128, 0, 0, 0.1);
    }

    &:active {
      background: rgba(128, 0, 0, 0.15);
    }
  }

  @media (max-width: 768px) {
    th, td {
      padding: 0.75rem 1rem;
      font-size: 0.8rem;
    }

    th {
      font-size: 0.75rem;
    }
  }

  @media (max-width: 480px) {
    min-width: 700px;

    th, td {
      padding: 0.6rem 0.75rem;
    }
  }
`;

// Mobile Card View (alternative to table on very small screens)
const MobileCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border: 1px solid rgba(128, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.2s;

  &:active {
    transform: scale(0.98);
    background: rgba(128, 0, 0, 0.1);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .card-title {
    font-weight: 600;
    font-size: 0.95rem;
    color: white;
  }

  .card-body {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .card-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .label {
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.6);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .value {
      font-size: 0.9rem;
      color: white;
      font-weight: 600;
    }
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => {
    if (props.status === 'active') return 'rgba(76, 175, 80, 0.2)';
    if (props.status === 'pending') return 'rgba(255, 193, 7, 0.2)';
    if (props.status === 'paused') return 'rgba(255, 152, 0, 0.2)';
    if (props.status === 'deleted') return 'rgba(128, 128, 128, 0.2)';
    if (props.status === 'inactive') return 'rgba(255, 68, 68, 0.2)';
    if (props.status === 'warning') return 'rgba(255, 193, 7, 0.2)';
    return 'rgba(128, 0, 0, 0.2)';
  }};
  color: ${props => {
    if (props.status === 'active') return '#4CAF50';
    if (props.status === 'pending') return '#FFC107';
    if (props.status === 'paused') return '#FF9800';
    if (props.status === 'deleted') return '#808080';
    if (props.status === 'inactive') return '#FF4444';
    if (props.status === 'warning') return '#FFC107';
    return '#800000';
  }};

  @media (max-width: 768px) {
    padding: 0.2rem 0.6rem;
    font-size: 0.7rem;

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const ExpandedDetails = styled.tr`
  background: rgba(0, 0, 0, 0.6);

  td {
    padding: 0 !important;
  }
`;

const DetailsContent = styled.div`
  padding: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const DetailSection = styled.div`
  h4 {
    color: #800000;
    font-size: 1rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .detail-grid {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    .label {
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.85rem;
    }

    .value {
      color: rgba(255, 255, 255, 0.9);
      font-weight: 600;
      font-size: 0.9rem;
    }
  }

  @media (max-width: 768px) {
    h4 {
      font-size: 0.95rem;
    }

    .detail-item {
      .label {
        font-size: 0.8rem;
      }

      .value {
        font-size: 0.85rem;
      }
    }
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const QuickActionButton = styled.button`
  background: ${props => {
    if (props.variant === 'danger') return 'rgba(244, 67, 54, 0.2)';
    if (props.variant === 'warning') return 'rgba(255, 152, 0, 0.2)';
    if (props.variant === 'success') return 'rgba(76, 175, 80, 0.2)';
    return 'rgba(128, 0, 0, 0.2)';
  }};
  color: ${props => {
    if (props.variant === 'danger') return '#F44336';
    if (props.variant === 'warning') return '#FF9800';
    if (props.variant === 'success') return '#4CAF50';
    return '#800000';
  }};
  border: 1px solid ${props => {
    if (props.variant === 'danger') return 'rgba(244, 67, 54, 0.3)';
    if (props.variant === 'warning') return 'rgba(255, 152, 0, 0.3)';
    if (props.variant === 'success') return 'rgba(76, 175, 80, 0.3)';
    return 'rgba(128, 0, 0, 0.3)';
  }};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 0.45rem 0.75rem;
    font-size: 0.8rem;
    flex: 1;
    min-width: 0;
    justify-content: center;

    svg {
      width: 14px;
      height: 14px;
    }

    &:hover {
      transform: none;
    }
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.75rem;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-end;
  }
`;

const ModalContent = styled.div`
  background: #1a1a2e;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  border: 1px solid ${props => {
    if (props.variant === 'danger') return 'rgba(244, 67, 54, 0.3)';
    if (props.variant === 'warning') return 'rgba(255, 193, 7, 0.3)';
    return 'rgba(128, 0, 0, 0.3)';
  }};
  max-height: 90vh;
  overflow-y: auto;

  h3 {
    color: ${props => {
      if (props.variant === 'danger') return '#F44336';
      if (props.variant === 'warning') return '#FFC107';
      return '#800000';
    }};
    margin: 0 0 1rem 0;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  .modal-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
  }

  .modal-button {
    flex: 1;
    padding: 0.75rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    &.confirm {
      background: ${props => {
        if (props.variant === 'danger') return '#F44336';
        if (props.variant === 'warning') return '#FFC107';
        return '#800000';
      }};
      color: white;

      &:hover {
        opacity: 0.9;
      }

      &:active {
        transform: scale(0.98);
      }
    }

    &.cancel {
      background: rgba(255, 255, 255, 0.1);
      color: white;

      &:hover {
        background: rgba(255, 255, 255, 0.15);
      }

      &:active {
        transform: scale(0.98);
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  @media (max-width: 768px) {
    border-radius: 12px 12px 0 0;
    padding: 1.5rem;
    max-height: 85vh;

    h3 {
      font-size: 1.3rem;
    }

    p {
      font-size: 0.9rem;
    }

    .modal-actions {
      gap: 0.75rem;
      flex-direction: column-reverse;

      .modal-button {
        width: 100%;
      }
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem;

    h3 {
      font-size: 1.2rem;
    }
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const PageLoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #800000;
  flex-direction: column;
  gap: 1rem;

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(128, 0, 0, 0.2);
    border-top-color: #800000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    padding: 3rem 1rem;

    .spinner {
      width: 36px;
      height: 36px;
      border-width: 3px;
    }
  }
`;

const EmptyState = styled.div`
  padding: 4rem 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);

  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 1rem;
    margin: 0;
  }

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;

    svg {
      width: 48px;
      height: 48px;
    }

    p {
      font-size: 0.9rem;
    }
  }
`;

const NotificationBanner = styled.div`
  background: ${props => {
    if (props.type === 'success') return 'rgba(76, 175, 80, 0.2)';
    if (props.type === 'error') return 'rgba(244, 67, 54, 0.2)';
    if (props.type === 'warning') return 'rgba(255, 193, 7, 0.2)';
    return 'rgba(33, 150, 243, 0.2)';
  }};
  border: 1px solid ${props => {
    if (props.type === 'success') return 'rgba(76, 175, 80, 0.3)';
    if (props.type === 'error') return 'rgba(244, 67, 54, 0.3)';
    if (props.type === 'warning') return 'rgba(255, 193, 7, 0.3)';
    return 'rgba(33, 150, 243, 0.3)';
  }};
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: space-between;

  .message {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
  }

  button {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    font-size: 1.5rem;
    opacity: 0.7;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    
    &:hover {
      opacity: 1;
    }

    &:active {
      transform: scale(0.95);
    }
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.85rem;
    gap: 0.75rem;

    .message {
      gap: 0.5rem;

      svg {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
      }
    }

    button {
      font-size: 1.25rem;
    }
  }

  @media (max-width: 480px) {
    padding: 0.65rem 0.75rem;
    font-size: 0.8rem;
  }
`;

const ShopCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  padding: 1.25rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    padding: 0.75rem;
  }
`;

const ShopCardItem = styled.div`
  background: rgba(255,255,255,0.04);
  border: 1px solid ${props => props.$hasBalance ? 'rgba(255,68,68,0.35)' : props.$paused ? 'rgba(255,152,0,0.25)' : 'rgba(128,0,0,0.22)'};
  border-radius: 12px;
  padding: 1rem 1.1rem 0.85rem;
  cursor: pointer;
  transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.35);
    border-color: rgba(128,0,0,0.55);
  }

  .card-top {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .avatar {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    flex-shrink: 0;
    overflow: hidden;
    background: rgba(128,0,0,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.1rem;
    color: #cc3333;
    border: 2px solid rgba(128,0,0,0.3);

    img { width: 100%; height: 100%; object-fit: cover; }
  }

  .info {
    flex: 1;
    min-width: 0;

    .name {
      font-weight: 700;
      font-size: 0.95rem;
      color: #fff;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .username {
      font-size: 0.75rem;
      color: rgba(255,255,255,0.45);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .email {
      font-size: 0.72rem;
      color: rgba(100,180,255,0.75);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 0.1rem;
    }
  }

  .badges {
    display: flex;
    gap: 0.35rem;
    flex-wrap: wrap;
    align-items: center;
  }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
    background: rgba(0,0,0,0.2);
    border-radius: 8px;
    padding: 0.6rem 0.5rem;
  }

  .stat-cell {
    text-align: center;
    .val {
      font-weight: 700;
      font-size: 0.95rem;
      color: #fff;
    }
    .lbl {
      font-size: 0.65rem;
      color: rgba(255,255,255,0.4);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-top: 0.1rem;
    }
  }

  .card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.72rem;
    color: rgba(255,255,255,0.35);
    padding-top: 0.4rem;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
`;

const DetailDrawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(480px, 100vw);
  background: #111128;
  border-left: 1px solid rgba(128,0,0,0.3);
  z-index: 500;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 32px rgba(0,0,0,0.5);
  animation: slideIn 0.22s ease;

  @keyframes slideIn {
    from { transform: translateX(100%); }
    to   { transform: translateX(0); }
  }

  .drawer-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    background: rgba(128,0,0,0.12);
    border-bottom: 1px solid rgba(128,0,0,0.2);
    flex-shrink: 0;

    .drawer-title {
      flex: 1;
      min-width: 0;
      .name { font-weight: 700; font-size: 1rem; color: #fff; }
      .sub  { font-size: 0.75rem; color: rgba(255,255,255,0.45); }
    }

    .close-btn {
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.12);
      color: #fff;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      flex-shrink: 0;
      &:hover { background: rgba(255,255,255,0.1); }
    }
  }

  .drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    scrollbar-width: thin;
    scrollbar-color: rgba(128,0,0,0.4) transparent;
  }
`;

const DrawerSection = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 10px;
  overflow: hidden;

  .section-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 0.85rem;
    background: rgba(128,0,0,0.1);
    border-bottom: 1px solid rgba(255,255,255,0.06);
    font-size: 0.78rem;
    font-weight: 700;
    color: #cc4444;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .section-rows {
    display: flex;
    flex-direction: column;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.55rem 0.85rem;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    font-size: 0.82rem;
    &:last-child { border-bottom: none; }

    .k { color: rgba(255,255,255,0.5); }
    .v { color: #fff; font-weight: 600; text-align: right; max-width: 60%; word-break: break-word; }
  }
`;

// ── Mobile-responsive layout helpers ────────────────────────────────────────

const SectionPad = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.75rem;
  }
`;

const ResponsiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${props => props.$min || '250px'}, 1fr));
  gap: ${props => props.$gap || '1.5rem'};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const StripeModeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const MilestoneRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 6px;
  font-size: 0.88rem;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: flex-start;
    font-size: 0.82rem;

    span:last-child {
      opacity: 0.85;
    }
  }
`;

const TopPerformerCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid rgba(128, 0, 0, 0.15);

  &:last-child {
    border-bottom: none;
  }

  .rank {
    font-weight: 700;
    font-size: 0.8rem;
    min-width: 28px;
    color: rgba(255,255,255,0.5);
  }

  .shop-info {
    flex: 1;
    min-width: 0;

    .shop-name {
      font-weight: 600;
      font-size: 0.9rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .shop-handle {
      font-size: 0.72rem;
      opacity: 0.5;
    }
  }

  .stats-pill {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.15rem;
    flex-shrink: 0;

    .revenue {
      font-weight: 700;
      font-size: 0.88rem;
      color: #4CAF50;
    }

    .sub {
      font-size: 0.68rem;
      opacity: 0.55;
    }
  }
`;

// ── My Shop tab styled components ───────────────────────────────────────────

const MyShopHero = styled.div`
  background: rgba(0,0,0,0.45);
  border: 1px solid rgba(128,0,0,0.25);
  border-radius: 14px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  .hero-avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    flex-shrink: 0;
    border: 3px solid rgba(128,0,0,0.45);
    background: rgba(128,0,0,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
    font-weight: 700;
    color: #cc3333;
    overflow: hidden;
    img { width: 100%; height: 100%; object-fit: cover; }
  }

  .hero-info {
    flex: 1;
    min-width: 0;
    .shop-name { font-size: 1.4rem; font-weight: 700; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .shop-sub  { font-size: 0.82rem; color: rgba(255,255,255,0.45); margin-top: 0.2rem; }
    .hero-badges { display: flex; gap: 0.4rem; flex-wrap: wrap; margin-top: 0.5rem; }
  }

  .hero-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    flex-shrink: 0;
  }

  @media (max-width: 600px) {
    padding: 1rem;
    gap: 1rem;
    .hero-avatar { width: 56px; height: 56px; font-size: 1.4rem; }
    .hero-info .shop-name { font-size: 1.15rem; }
    .hero-actions { width: 100%; button { flex: 1; } }
  }
`;

const StatsBar = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.25rem;

  &::-webkit-scrollbar { height: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(128,0,0,0.4); border-radius: 2px; }
`;

const StatChip = styled.div`
  flex-shrink: 0;
  background: rgba(0,0,0,0.4);
  border: 1px solid rgba(128,0,0,0.22);
  border-radius: 10px;
  padding: 0.6rem 0.9rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  min-width: 80px;

  .chip-val {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${props => props.$color || '#fff'};
    white-space: nowrap;
  }

  .chip-lbl {
    font-size: 0.62rem;
    color: rgba(255,255,255,0.45);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }
`;

const MyShopGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const MyShopCard = styled.div`
  background: rgba(0,0,0,0.4);
  border: 1px solid rgba(128,0,0,0.2);
  border-radius: 12px;
  overflow: hidden;

  .card-title {
    background: rgba(128,0,0,0.12);
    border-bottom: 1px solid rgba(128,0,0,0.18);
    padding: 0.6rem 0.9rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: #cc4444;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .card-rows {
    display: flex;
    flex-direction: column;
  }

  .card-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0.9rem;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    font-size: 0.82rem;
    &:last-child { border-bottom: none; }
    .k { color: rgba(255,255,255,0.5); }
    .v { color: #fff; font-weight: 600; text-align: right; max-width: 55%; word-break: break-word; }
  }

  .card-actions {
    padding: 0.75rem 0.9rem;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }
`;

const QuickLink = styled.button`
  width: 100%;
  background: rgba(128,0,0,0.1);
  border: 1px solid rgba(128,0,0,0.22);
  color: rgba(255,255,255,0.85);
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
  font-size: 0.83rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
  transition: all 0.15s;

  &:hover {
    background: rgba(128,0,0,0.2);
    border-color: rgba(128,0,0,0.4);
    color: #fff;
  }

  &:active { transform: scale(0.98); }

  svg { flex-shrink: 0; opacity: 0.75; }

  span.arrow {
    margin-left: auto;
    opacity: 0.4;
    font-size: 0.75rem;
  }
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.9rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:last-child { border-bottom: none; }

  .item-img {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    object-fit: cover;
    flex-shrink: 0;
    background: rgba(128,0,0,0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    overflow: hidden;
    img { width: 100%; height: 100%; object-fit: cover; }
  }

  .item-info {
    flex: 1;
    min-width: 0;
    .item-name { font-size: 0.85rem; font-weight: 600; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .item-meta { font-size: 0.7rem; color: rgba(255,255,255,0.4); margin-top: 0.1rem; }
  }

  .item-price {
    font-size: 0.85rem;
    font-weight: 700;
    color: #4CAF50;
    flex-shrink: 0;
  }

  .item-vis {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${props => props.$visible ? '#4CAF50' : 'rgba(255,255,255,0.2)'};
  }
`;

// Rest of the component code remains the same, just using the mobile-optimized styled components
const ShopDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stripeFilter, setStripeFilter] = useState('all');
  const [notification, setNotification] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Expanded shop details
  const [expandedShopId, setExpandedShopId] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
const [showRequestsModal, setShowRequestsModal] = useState(false);
const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Modal states
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [processingAction, setProcessingAction] = useState(false);

  const [showBalanceModal, setShowBalanceModal] = useState(false);
const [balanceMode, setBalanceMode] = useState('add'); // 'add' | 'set'
const [showPromoModal, setShowPromoModal] = useState(false);
const [stripeMode, setStripeMode] = useState('live');
const [stripeModeLoading, setStripeModeLoading] = useState(false);
const [balanceOverride, setBalanceOverride] = useState('');
const [promoCodes, setPromoCodes] = useState([]);
const [newPromoCode, setNewPromoCode] = useState('');
const [newPromoDiscount, setNewPromoDiscount] = useState('');
const [newPromoLabel, setNewPromoLabel] = useState('');
const [promoLoading, setPromoLoading] = useState(false);
  
  // Analytics Data
  const [stats, setStats] = useState({
    totalShops: 0,
    activeShops: 0,
    pausedShops: 0,
    deletedShops: 0,
    totalItems: 0,
    totalServices: 0,
    totalUsers: 0,
    totalRevenue: 0,
    totalTransactions: 0,
    completedTransactions: 0,
    pendingTransactions: 0,
    avgTransactionValue: 0,
    totalPosts: 0,
    totalEvents: 0,
    totalImages: 0,
    totalVideos: 0,
    stripeActiveCount: 0,
    stripePendingCount: 0,
    stripeIncompleteCount: 0,
    outstandingBalance: 0,
    platformFeesEarned: 0,
    totalFollowers: 0,
    shopsNearMilestone: [],
  });

  const [shops, setShops] = useState([]);
  const [detailShop, setDetailShop] = useState(null);
  const [myShopData, setMyShopData] = useState(null);
  const [myShopLoading, setMyShopLoading] = useState(false);

  // Detect mobile resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load Stripe mode from Firestore on mount
  useEffect(() => {
    const loadStripeMode = async () => {
      try {
        const snap = await getDoc(doc(db, 'config', 'stripeMode'));
        if (snap.exists()) {
          setStripeMode(snap.data().mode || 'live');
        }
      } catch (err) {
        console.error('[dashboard] Failed to load stripe mode:', err);
      }
    };
    loadStripeMode();
  }, []);

  const handleStripeModeToggle = async () => {
    const newMode = stripeMode === 'live' ? 'test' : 'live';
    setStripeModeLoading(true);
    try {
      await setDoc(doc(db, 'config', 'stripeMode'), { mode: newMode });
      setStripeMode(newMode);
      resetStripeMode();
      setNotification({ type: 'success', message: `Stripe switched to ${newMode.toUpperCase()} mode` });
    } catch (err) {
      console.error('[dashboard] Failed to update stripe mode:', err);
      setNotification({ type: 'error', message: 'Failed to update Stripe mode' });
    } finally {
      setStripeModeLoading(false);
    }
  };

  // Fetch comprehensive shop analytics
  const fetchShopAnalytics = async (shopId) => {
    try {
      // Fetch transactions for this shop
      const transactionsRef = collection(db, 'transactions');
      const shopTransactionsQuery = query(
        transactionsRef,
        where('sellerId', '==', shopId)
      );
      const transactionsSnap = await getDocs(shopTransactionsQuery);
      
      let totalSales = 0;
      let completedSales = 0;
      let pendingTransactions = 0;
      let approvedTransactions = 0;
      let totalRevenue = 0;
      let cancelledCount = 0;

      transactionsSnap.docs.forEach(doc => {
        const data = doc.data();
        totalSales++;

        if (data.status === 'completed') {
          completedSales++;
          totalRevenue += data.costs?.total || data.total || 0;
        }
        if (data.status === 'pending') pendingTransactions++;
        if (data.status === 'approved') approvedTransactions++;
        if (data.status === 'cancelled' || data.status === 'seller_rejected' || data.status === 'rejected') {
          cancelledCount++;
        }
      });

      // Fetch purchases (as buyer)
      const purchasesQuery = query(
        transactionsRef,
        where('buyerId', '==', shopId)
      );
      const purchasesSnap = await getDocs(purchasesQuery);
      const totalPurchases = purchasesSnap.docs.length;
      
      // Fetch chats for message count
      const chatsRef = collection(db, 'chats');
      const chatsQuery = query(
        chatsRef,
        where('sellerId', '==', shopId)
      );
      const chatsSnap = await getDocs(chatsQuery);
      
      let messagesSent = 0;
      let messagesReceived = 0;
      
      for (const chatDoc of chatsSnap.docs) {
        const messagesRef = collection(db, 'chats', chatDoc.id, 'messages');
        const messagesSnap = await getDocs(messagesRef);
        
        messagesSnap.docs.forEach(msgDoc => {
          const msg = msgDoc.data();
          if (msg.sender === shopId) {
            messagesSent++;
          } else {
            messagesReceived++;
          }
        });
      }
      
      // Fetch billing info
      const billingRef = doc(db, 'billing', shopId);
      const billingSnap = await getDoc(billingRef);
      const billingData = billingSnap.exists() ? billingSnap.data() : {};

      // Fetch owner profile from users collection
      let ownerData = {};
      try {
        const userSnap = await getDoc(doc(db, 'users', shopId));
        if (userSnap.exists()) ownerData = userSnap.data();
      } catch (_) { /* users doc may not exist yet */ }
      
      const completionRate = totalSales > 0 ? ((completedSales / totalSales) * 100).toFixed(1) : 0;
      const cancellationRate = totalSales > 0 ? ((cancelledCount / totalSales) * 100).toFixed(1) : 0;
      const avgOrderValue = completedSales > 0 ? (totalRevenue / completedSales).toFixed(2) : 0;

      // Accurate outstanding balance using billingTiers utility
      const outstandingBalance = calculateOutstandingBalance(billingData).toFixed(2);

      // Platform fee earned: 4.1% of completed revenue (7% total - 2.9% Stripe)
      const platformFeeEarned = (totalRevenue * 0.041).toFixed(2);

      // Next billing milestone info
      const billingTotalSales = billingData.totalSales || totalRevenue;
      const nextMilestone = getNextMilestone(billingTotalSales);
      const salesUntilNextMilestone = nextMilestone
        ? Math.max(0, nextMilestone.threshold - billingTotalSales).toFixed(2)
        : null;

      // Fetch community posts count from subcollection for accuracy
      let postCount = 0;
      let eventCount = 0;
      try {
        const postsRef = collection(db, 'shops', shopId, 'communityPosts');
        const postsSnap = await getDocs(query(postsRef, where('deleted', '==', false)));
        postsSnap.docs.forEach(p => {
          postCount++;
          if (p.data().type === 'event') eventCount++;
        });
      } catch (_) { /* subcollection may not exist */ }

      return {
        totalSales,
        completedSales,
        pendingTransactions,
        approvedTransactions,
        totalPurchases,
        totalRevenue: totalRevenue.toFixed(2),
        avgOrderValue,
        completionRate,
        cancellationRate,
        messagesSent,
        messagesReceived,
        platformFeeEarned,
        outstandingBalance,
        billingTotalSales: billingTotalSales.toFixed ? billingTotalSales.toFixed(2) : billingTotalSales,
        nextMilestoneFee: nextMilestone?.fee || 0,
        nextMilestoneThreshold: nextMilestone?.threshold || 0,
        salesUntilNextMilestone,
        postCount,
        eventCount,
        ownerDisplayName: ownerData.displayName || '',
        ownerEmail: ownerData.email || '',
        ownerPhone: ownerData.phone || '',
        ownerAvatar: ownerData.avatar || ownerData.profilePic || '',
      };
    } catch (error) {
      console.error('Error fetching shop analytics:', error);
      return {};
    }
  };

  const fetchPromoCodes = async () => {
  try {
    const promoRef = doc(db, 'promoCodes', 'codes');
    const snap = await getDoc(promoRef);
    if (snap.exists()) setPromoCodes(snap.data().list || []);
  } catch (err) {
    console.error('Error fetching promo codes:', err);
  }
};

const handleBalanceOverride = async () => {
  if (!selectedShop || balanceOverride === '' || processingAction) return;
  setProcessingAction(true);
  try {
    const billingRef = doc(db, 'billing', selectedShop.id);
    const billingSnap = await getDoc(billingRef);
    const billingData = billingSnap.exists() ? billingSnap.data() : {};

    const totalSales = billingData.totalSales || 0;

    const { getCurrentMilestone } = await import('../../utils/billingTiers');
    const currentMilestone = getCurrentMilestone(totalSales);
    const shouldHavePaid = SHOP_OPENING_FEE + (currentMilestone?.cumulativeTotal || 0);

    const inputAmount = parseFloat(balanceOverride);
    const currentBalance = parseFloat(selectedShop.analytics?.outstandingBalance || 0);
    const newBalance = balanceMode === 'add'
      ? parseFloat((currentBalance + inputAmount).toFixed(2))
      : inputAmount;

    const newTotalPaid = Math.max(0, shouldHavePaid - newBalance);

    const overrideRecord = {
      amount: newTotalPaid,
      actualCharged: 0,
      date: new Date().toISOString(),
      paymentIntentId: `admin_override_${Date.now()}`,
      type: 'admin_override',
      adminNote: balanceMode === 'add'
        ? `Admin added $${inputAmount.toFixed(2)} to balance (was $${currentBalance.toFixed(2)}, now $${newBalance.toFixed(2)})`
        : `Admin set balance to $${newBalance.toFixed(2)}`,
      adminId: user.uid
    };

    await updateDoc(billingRef, {
      'paymentHistory.paidMilestoneFees': [newTotalPaid],
      'paymentHistory.payments': arrayUnion(overrideRecord),
      itemsHiddenDueToBalance: newBalance > 0,
      updatedAt: serverTimestamp(),
      adminBalanceOverride: {
        by: user.uid,
        at: new Date().toISOString(),
        newBalance,
        previousData: billingData.paymentHistory?.paidMilestoneFees || []
      }
    });

    if (newBalance === 0) {
      await fetch('/api/process-billing-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation: 'resume_account', userId: selectedShop.id })
      });
    }

    const verb = balanceMode === 'add' ? `increased by $${inputAmount.toFixed(2)} to` : 'set to';
    showNotification('success', `Balance for "${selectedShop.name}" ${verb} $${newBalance.toFixed(2)}`);
    setShowBalanceModal(false);
    setBalanceOverride('');
    setSelectedShop(null);
    await fetchDashboardData();
  } catch (err) {
    console.error('Error overriding balance:', err);
    showNotification('error', err.message);
  } finally {
    setProcessingAction(false);
  }
};

const handleSavePromoCode = async () => {
  if (!newPromoCode.trim() || !newPromoDiscount) return;
  setPromoLoading(true);
  try {
    const promoRef = doc(db, 'promoCodes', 'codes');
    const snap = await getDoc(promoRef);
    const existing = snap.exists() ? (snap.data().list || []) : [];

    const newEntry = {
      code: newPromoCode.trim().toLowerCase(),
      discountPercent: parseInt(newPromoDiscount),
      label: newPromoLabel.trim() || `${newPromoDiscount}% off`,
      active: true,
      createdAt: new Date().toISOString(),
      createdBy: user.uid
    };

    const updated = [...existing.filter(p => p.code !== newEntry.code), newEntry];
    await setDoc(doc(db, 'promoCodes', 'codes'), { list: updated });

    setPromoCodes(updated);
    setNewPromoCode('');
    setNewPromoDiscount('');
    setNewPromoLabel('');
    showNotification('success', `Promo code "${newEntry.code}" saved!`);
  } catch (err) {
    showNotification('error', err.message);
  } finally {
    setPromoLoading(false);
  }
};

const handleDeletePromoCode = async (code) => {
  try {
    const updated = promoCodes.filter(p => p.code !== code);
    await setDoc(doc(db, 'promoCodes', 'codes'), { list: updated });
    setPromoCodes(updated);
    showNotification('success', `Promo code "${code}" deleted`);
  } catch (err) {
    showNotification('error', err.message);
  }
};

const handleTogglePromoActive = async (code) => {
  try {
    const updated = promoCodes.map(p =>
      p.code === code ? { ...p, active: !p.active } : p
    );
    await setDoc(doc(db, 'promoCodes', 'codes'), { list: updated });
    setPromoCodes(updated);
    showNotification('success', `Promo code "${code}" ${updated.find(p => p.code === code)?.active ? 'enabled' : 'disabled'}`);
  } catch (err) {
    showNotification('error', err.message);
  }
};

// ── My Shop tab ──────────────────────────────────────────────────────────────

const fetchMyShop = async () => {
  if (!user?.uid || myShopData || myShopLoading) return;
  setMyShopLoading(true);
  try {
    const shopSnap = await getDoc(doc(db, 'shops', user.uid));
    if (shopSnap.exists()) {
      const shopDoc = { id: shopSnap.id, ...shopSnap.data() };
      const analytics = await fetchShopAnalytics(user.uid);
      setMyShopData({ ...shopDoc, analytics });
    }
  } catch (err) {
    console.error('[my-shop] fetch error:', err);
  } finally {
    setMyShopLoading(false);
  }
};

const renderMyShop = () => {
  if (myShopLoading) return (
    <PageLoadingSpinner>
      <div className="spinner" />
      <span>Loading your shop…</span>
    </PageLoadingSpinner>
  );

  if (!myShopData) return (
    <EmptyState>
      <Store />
      <p>No shop found for your account.</p>
      <ActionButton primary style={{ marginTop: '1rem' }} onClick={() => navigate('/shop/manage')}>
        <ChevronRight size={16} /> Set up your shop
      </ActionButton>
    </EmptyState>
  );

  const s = myShopData;
  const profileImg = typeof s.profile === 'string' ? s.profile : s.profile?.image || null;
  const initials   = (s.name || '?').charAt(0).toUpperCase();
  const items      = (s.items    || []).filter(i => !i.deleted);
  const services   = (s.services || []).filter(sv => !sv.deleted);
  const balance    = parseFloat(s.analytics?.outstandingBalance || 0);
  const revenue    = parseFloat(s.analytics?.totalRevenue || 0);

  const JOB_ICONS = { rides:'🚗', home_repair:'🔧', cleaning:'🧹', moving:'📦',
    delivery:'🚚', tutoring:'📚', pet_care:'🐾', handyman:'🛠️',
    beauty:'💇', tech:'💻', events:'🎉', other:'💼' };

  return (
    <>
      {/* ── Hero ── */}
      <MyShopHero>
        <div className="hero-avatar">
          {profileImg ? <img src={profileImg} alt={s.name} /> : initials}
        </div>
        <div className="hero-info">
          <div className="shop-name">{s.name || 'My Shop'}</div>
          <div className="shop-sub">@{s.username || 'N/A'} · {s.storeType || 'general'} · {s.city || 'No location'}</div>
          <div className="hero-badges">
            <Badge status={s.status || 'active'}>
              {s.status === 'active' && <CheckCircle size={10} />}
              {s.status === 'paused' && <Pause size={10} />}
              {s.status || 'active'}
            </Badge>
            <Badge status={s.stripeReady ? 'active' : s.stripeAccountStatus === 'pending_verification' ? 'pending' : 'inactive'}>
              <CreditCard size={10} />
              {s.stripeReady ? 'Stripe Active' : s.stripeAccountStatus === 'pending_verification' ? 'Stripe Pending' : 'No Stripe'}
            </Badge>
            {balance > 0 && (
              <Badge status="warning"><AlertTriangle size={10} />${balance.toFixed(2)} due</Badge>
            )}
          </div>
        </div>
        <div className="hero-actions">
          <ActionButton onClick={() => window.open(`/${s.username}`, '_blank')}>
            <Eye size={15} /> View Live
          </ActionButton>
          <ActionButton primary onClick={() => navigate('/shop/manage')}>
            <Edit3 size={15} /> Manage
          </ActionButton>
        </div>
      </MyShopHero>

      {/* ── Stats bar ── */}
      <StatsBar>
        <StatChip $color="#4CAF50">
          <span className="chip-val">${revenue.toFixed(0)}</span>
          <span className="chip-lbl">Revenue</span>
        </StatChip>
        <StatChip>
          <span className="chip-val">{s.analytics?.completedSales || 0}</span>
          <span className="chip-lbl">Sales</span>
        </StatChip>
        <StatChip $color={s.analytics?.pendingTransactions > 0 ? '#FFC107' : '#fff'}>
          <span className="chip-val">{s.analytics?.pendingTransactions || 0}</span>
          <span className="chip-lbl">Pending</span>
        </StatChip>
        <StatChip>
          <span className="chip-val">{items.length}</span>
          <span className="chip-lbl">Products</span>
        </StatChip>
        <StatChip>
          <span className="chip-val">{services.length}</span>
          <span className="chip-lbl">Services</span>
        </StatChip>
        <StatChip>
          <span className="chip-val">{(s.followers || []).length}</span>
          <span className="chip-lbl">Followers</span>
        </StatChip>
        <StatChip>
          <span className="chip-val">{s.analytics?.completionRate || 0}%</span>
          <span className="chip-lbl">Completion</span>
        </StatChip>
        <StatChip $color={balance > 0 ? '#F44336' : '#4CAF50'}>
          <span className="chip-val">${balance.toFixed(0)}</span>
          <span className="chip-lbl">Balance Due</span>
        </StatChip>
      </StatsBar>

      {/* ── 3-column card grid ── */}
      <MyShopGrid>

        {/* Financials */}
        <MyShopCard>
          <div className="card-title"><DollarSign size={12} />Financials</div>
          <div className="card-rows">
            <div className="card-row"><span className="k">Total Revenue</span><span className="v" style={{ color: '#4CAF50' }}>${s.analytics?.totalRevenue || '0.00'}</span></div>
            <div className="card-row"><span className="k">Avg Order Value</span><span className="v">${s.analytics?.avgOrderValue || '0.00'}</span></div>
            <div className="card-row"><span className="k">Completed Sales</span><span className="v">{s.analytics?.completedSales || 0}</span></div>
            <div className="card-row"><span className="k">Pending Orders</span><span className="v" style={{ color: s.analytics?.pendingTransactions > 0 ? '#FFC107' : 'inherit' }}>{s.analytics?.pendingTransactions || 0}</span></div>
            <div className="card-row"><span className="k">Platform Fees (4.1%)</span><span className="v" style={{ color: '#00C853' }}>${s.analytics?.platformFeeEarned || '0.00'}</span></div>
            <div className="card-row">
              <span className="k">Outstanding Balance</span>
              <span className="v" style={{ color: balance > 0 ? '#F44336' : '#4CAF50' }}>${balance.toFixed(2)}</span>
            </div>
            <div className="card-row"><span className="k">Billing Sales</span><span className="v">${s.analytics?.billingTotalSales || '0.00'}</span></div>
            <div className="card-row">
              <span className="k">Next Milestone</span>
              <span className="v" style={{ fontSize: '0.75rem' }}>
                {s.analytics?.nextMilestoneThreshold
                  ? `$${s.analytics.nextMilestoneFee} fee @ $${s.analytics.nextMilestoneThreshold}`
                  : '—'}
              </span>
            </div>
          </div>
        </MyShopCard>

        {/* Stripe & Shop Info */}
        <MyShopCard>
          <div className="card-title"><CreditCard size={12} />Stripe & Store</div>
          <div className="card-rows">
            <div className="card-row"><span className="k">Stripe Status</span><span className="v">{s.stripeReady ? '✅ Active' : s.stripeAccountStatus === 'pending_verification' ? '⏳ Pending' : s.stripeAccountId ? '⚠️ Incomplete' : '❌ Not set up'}</span></div>
            <div className="card-row"><span className="k">Store Type</span><span className="v" style={{ textTransform: 'capitalize' }}>{s.storeType || 'general'}</span></div>
            <div className="card-row"><span className="k">City</span><span className="v">{s.city || '—'}</span></div>
            <div className="card-row"><span className="k">Followers</span><span className="v">{(s.followers || []).length}</span></div>
            <div className="card-row"><span className="k">Following</span><span className="v">{(s.following || []).length}</span></div>
            <div className="card-row"><span className="k">Posts</span><span className="v">{s.analytics?.postCount || 0}</span></div>
            <div className="card-row"><span className="k">Completion Rate</span><span className="v">{s.analytics?.completionRate || 0}%</span></div>
            <div className="card-row"><span className="k">Member Since</span><span className="v">{s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }) : '—'}</span></div>
          </div>
        </MyShopCard>

        {/* Quick Links */}
        <MyShopCard>
          <div className="card-title"><Settings size={12} />Quick Actions</div>
          <div className="card-actions">
            <QuickLink onClick={() => navigate('/shop/manage')}>
              <Edit3 size={14} /> Edit Shop Profile <span className="arrow">›</span>
            </QuickLink>
            <QuickLink onClick={() => navigate('/shop/manage?tab=items')}>
              <Package size={14} /> Manage Products <span className="arrow">›</span>
            </QuickLink>
            <QuickLink onClick={() => navigate('/shop/manage?tab=services')}>
              <Activity size={14} /> Manage Services <span className="arrow">›</span>
            </QuickLink>
            <QuickLink onClick={() => navigate('/shop/manage?tab=orders')}>
              <ShoppingBag size={14} /> View Orders <span className="arrow">›</span>
            </QuickLink>
            <QuickLink onClick={() => navigate('/shop/manage?tab=community')}>
              <MessageCircle size={14} /> Community Posts <span className="arrow">›</span>
            </QuickLink>
            <QuickLink onClick={() => window.open(`/${s.username}`, '_blank')}>
              <ExternalLink size={14} /> View Public Page <span className="arrow">›</span>
            </QuickLink>
            {balance > 0 && (
              <QuickLink onClick={() => navigate('/shop/manage?tab=billing')} style={{ borderColor: 'rgba(244,67,54,0.35)', background: 'rgba(244,67,54,0.08)', color: '#FF6B6B' }}>
                <AlertTriangle size={14} /> Pay Outstanding Balance <span className="arrow">›</span>
              </QuickLink>
            )}
          </div>
        </MyShopCard>
      </MyShopGrid>

      {/* ── Items & Services preview ── */}
      {(items.length > 0 || services.length > 0) && (
        <MyShopCard style={{ marginBottom: '1rem' }}>
          <div className="card-title"><Package size={12} />Products & Services ({items.length + services.length} total)</div>

          {/* Products */}
          {items.slice(0, 5).map(item => {
            const img = Array.isArray(item.images) ? item.images.find(i => typeof i === 'string' && i.startsWith('http')) : null;
            return (
              <ItemRow key={item.id} $visible={item.visible !== false}>
                <div className="item-img">
                  {img ? <img src={img} alt={item.name} /> : '📦'}
                </div>
                <div className="item-info">
                  <div className="item-name">{item.name || 'Unnamed item'}</div>
                  <div className="item-meta">{item.category || 'Product'} · qty {item.quantity ?? '—'}</div>
                </div>
                <div className="item-price">{formatShopPrice(item.price || 0, s.currency)}</div>
                <div className="item-vis" title={item.visible !== false ? 'Visible' : 'Hidden'} />
              </ItemRow>
            );
          })}

          {/* Services */}
          {services.slice(0, 5).map(sv => {
            const img = Array.isArray(sv.images) ? sv.images.find(i => typeof i === 'string' && i.startsWith('http')) : null;
            const icon = JOB_ICONS[sv.jobType] || '🛠️';
            return (
              <ItemRow key={sv.id} $visible={sv.visible !== false}>
                <div className="item-img">
                  {img ? <img src={img} alt={sv.name} /> : icon}
                </div>
                <div className="item-info">
                  <div className="item-name">{sv.name || 'Unnamed service'}</div>
                  <div className="item-meta">{sv.category || sv.jobType || 'Service'} · {sv.slots ?? 1} slot{(sv.slots ?? 1) !== 1 ? 's' : ''}</div>
                </div>
                <div className="item-price">{formatShopPrice(sv.price || 0, s.currency)}</div>
                <div className="item-vis" title={sv.visible !== false ? 'Visible' : 'Hidden'} />
              </ItemRow>
            );
          })}

          {(items.length + services.length) > 10 && (
            <div style={{ padding: '0.75rem 0.9rem', textAlign: 'center' }}>
              <QuickLink style={{ display: 'inline-flex', width: 'auto', padding: '0.45rem 1.25rem' }} onClick={() => navigate('/shop/manage')}>
                View all {items.length + services.length} listings →
              </QuickLink>
            </div>
          )}
        </MyShopCard>
      )}

      {/* ── Refresh nudge ── */}
      <div style={{ textAlign: 'center', paddingTop: '0.5rem' }}>
        <QuickLink style={{ display: 'inline-flex', width: 'auto', padding: '0.45rem 1rem', fontSize: '0.78rem', opacity: 0.6 }}
          onClick={() => { setMyShopData(null); fetchMyShop(); }}>
          <RefreshCw size={12} /> Refresh my shop data
        </QuickLink>
      </div>
    </>
  );
};

const renderPromoCodes = () => (
  <>
    <DataTable>
      <TableHeader>
        <h3>🏷️ Create Promo Code</h3>
      </TableHeader>
      <SectionPad>
        <ResponsiveGrid $min="170px" $gap="1rem" style={{ marginBottom: '1.25rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.82rem', opacity: 0.7 }}>Code Phrase *</label>
            <SearchInput
              placeholder="e.g. LAUNCH2024"
              value={newPromoCode}
              onChange={e => setNewPromoCode(e.target.value.toUpperCase())}
              style={{ width: '100%', fontFamily: 'monospace' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.82rem', opacity: 0.7 }}>Discount % (0–100) *</label>
            <SearchInput
              type="number"
              min="0" max="100"
              placeholder="e.g. 50"
              value={newPromoDiscount}
              onChange={e => setNewPromoDiscount(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.82rem', opacity: 0.7 }}>Display Label (optional)</label>
            <SearchInput
              placeholder="e.g. Early bird 50% off"
              value={newPromoLabel}
              onChange={e => setNewPromoLabel(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </ResponsiveGrid>
        <ActionButton
          primary
          onClick={handleSavePromoCode}
          disabled={!newPromoCode.trim() || !newPromoDiscount || promoLoading}
        >
          {promoLoading ? <LoadingSpinner /> : '+ Add Promo Code'}
        </ActionButton>
        <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', opacity: 0.6 }}>
          100% discount = free access, no Stripe charge. Shop balance is still marked as paid.
        </p>
      </SectionPad>
    </DataTable>

    <DataTable>
      <TableHeader>
        <h3>Active Promo Codes ({promoCodes.length})</h3>
      </TableHeader>
      {promoCodes.length === 0 ? (
        <EmptyState>
          <DollarSign />
          <p>No promo codes created yet</p>
        </EmptyState>
      ) : (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Label</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promoCodes.map(promo => (
                <tr key={promo.code} onClick={() => {}}>
                  <td>
                    <code style={{ background: 'rgba(255,255,255,0.1)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.9rem', letterSpacing: '0.05em' }}>
                      {promo.code.toUpperCase()}
                    </code>
                  </td>
                  <td>
                    <strong style={{ color: promo.discountPercent === 100 ? '#4CAF50' : promo.discountPercent >= 50 ? '#FFC107' : 'white', fontSize: '1.1rem' }}>
                      {promo.discountPercent}%
                    </strong>
                    {promo.discountPercent === 100 && <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: '#4CAF50' }}>FREE</span>}
                  </td>
                  <td style={{ opacity: 0.8 }}>{promo.label || '—'}</td>
                  <td>
                    <Badge status={promo.active ? 'active' : 'inactive'}>
                      {promo.active ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                      {promo.active ? 'Active' : 'Disabled'}
                    </Badge>
                  </td>
                  <td style={{ opacity: 0.7 }}>
                    {promo.createdAt ? new Date(promo.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <QuickActionButton variant="warning" onClick={() => handleTogglePromoActive(promo.code)}>
                        {promo.active ? <Pause size={14} /> : <Play size={14} />}
                        {promo.active ? 'Disable' : 'Enable'}
                      </QuickActionButton>
                      
                      <QuickActionButton variant="danger" onClick={() => handleDeletePromoCode(promo.code)}>
                        <Trash2 size={14} />
                        Delete
                      </QuickActionButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}
    </DataTable>
  </>
);

  const processAccountRequest = async (requestId, action) => {
  if (!selectedRequest || processingAction) return;
  
  try {
    setProcessingAction(true);
    
    const request = selectedRequest;
    let apiOperation = null;
    
    // Determine API operation based on request type
    if (request.requestType === 'pause_request') {
      apiOperation = 'pause_account';
    } else if (request.requestType === 'resume_request') {
      apiOperation = 'resume_account';
    } else if (request.requestType === 'delete_request') {
      apiOperation = 'delete_account';
    }
    
    if (action === 'approve' && apiOperation) {
      // Call the billing API to perform action
      const response = await fetch('/api/process-billing-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: apiOperation,
          userId: request.userId
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to process request');
      }
      
      // Update request status
      const requestRef = doc(db, 'accountRequests', requestId);
      await updateDoc(requestRef, {
        status: 'approved',
        processedBy: user.uid,
        processedAt: serverTimestamp()
      });
      
      // Create notification for user
      const notifRef = collection(db, 'notifications');
      await addDoc(notifRef, {
        userId: request.userId,
        type: 'account_request_approved',
        title: `${request.requestType.replace('_', ' ')} Approved`,
        message: `Your ${request.requestType.replace('_request', '')} request has been approved by admin.`,
        read: false,
        createdAt: serverTimestamp()
      });
      
      showNotification('success', `Request approved: ${request.requestType}`);
      
    } else if (action === 'reject') {
      // Update request status
      const requestRef = doc(db, 'accountRequests', requestId);
      await updateDoc(requestRef, {
        status: 'rejected',
        processedBy: user.uid,
        processedAt: serverTimestamp()
      });
      
      // Create notification for user
      const notifRef = collection(db, 'notifications');
      await addDoc(notifRef, {
        userId: request.userId,
        type: 'account_request_rejected',
        title: `${request.requestType.replace('_', ' ')} Rejected`,
        message: `Your ${request.requestType.replace('_request', '')} request has been rejected. Please contact support for details.`,
        read: false,
        createdAt: serverTimestamp()
      });
      
      showNotification('warning', `Request rejected: ${request.requestType}`);
    }
    
    setShowRequestsModal(false);
    setSelectedRequest(null);
    await fetchDashboardData();
    
  } catch (error) {
    console.error('Error processing request:', error);
    showNotification('error', error.message);
  } finally {
    setProcessingAction(false);
  }
};

  // Fetch all dashboard data
  const fetchDashboardData = async (forceRefresh = false) => {
    // Return cached data immediately if available and not forcing a refresh
    if (!forceRefresh && _dashboardCache) {
      setShops(_dashboardCache.shops);
      setStats(_dashboardCache.stats);
      setPendingRequests(_dashboardCache.pendingRequests);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch all shops
      const shopsSnapshot = await getDocs(collection(db, 'shops'));
      const shopsData = await Promise.all(
        shopsSnapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const analytics = await fetchShopAnalytics(docSnap.id);
          
          return {
            id: docSnap.id,
            ...data,
            analytics
          };
        })
      );

      // Fetch pending account requests (admin only, once — not per-shop)
      let pendingRequestsData = [];
      try {
        const requestsSnap = await getDocs(query(
          collection(db, 'accountRequests'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        ));
        pendingRequestsData = requestsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPendingRequests(pendingRequestsData);
      } catch (e) {
        console.warn('Could not load account requests:', e.message);
      }

      // Calculate platform statistics
      let totalItems = 0;
      let totalServices = 0;
      let totalPosts = 0;
      let totalEvents = 0;
      let totalImages = 0;
      let totalVideos = 0;
      let activeShops = 0;
      let pausedShops = 0;
      let deletedShops = 0;
      let stripeActiveCount = 0;
      let stripePendingCount = 0;
      let stripeIncompleteCount = 0;
      let totalRevenue = 0;
      let totalTransactions = 0;
      let completedTransactions = 0;
      let pendingTransactions = 0;
      let outstandingBalance = 0;
      let platformFeesEarned = 0;
      let totalFollowers = 0;
      const shopsNearMilestone = [];

      shopsData.forEach(shop => {
        // Count by status
        if (shop.status === 'active') activeShops++;
        if (shop.status === 'paused') pausedShops++;
        if (shop.status === 'deleted') deletedShops++;
        
        // Stripe status
        if (shop.stripeReady) stripeActiveCount++;
        else if (shop.stripeAccountStatus === 'pending_verification') stripePendingCount++;
        else if (shop.stripeAccountId) stripeIncompleteCount++;
        
        // Items and services
        if (shop.items && Array.isArray(shop.items)) {
          const activeItems = shop.items.filter(item => !item.deleted);
          totalItems += activeItems.length;
          
          activeItems.forEach(item => {
            if (item.images) {
              totalImages += item.images.filter(img => img && typeof img === 'string').length;
            }
          });
        }

        if (shop.services && Array.isArray(shop.services)) {
          const activeServices = shop.services.filter(service => !service.deleted);
          totalServices += activeServices.length;
          
          activeServices.forEach(service => {
            if (service.images) {
              totalImages += service.images.filter(img => img && typeof img === 'string').length;
            }
          });
        }

        // Community posts — prefer subcollection count from analytics, fallback to embedded array
        if (shop.analytics?.postCount != null) {
          totalPosts += shop.analytics.postCount;
          totalEvents += shop.analytics.eventCount || 0;
        } else if (shop.communityPosts && Array.isArray(shop.communityPosts)) {
          const activePosts = shop.communityPosts.filter(post => !post.deleted);
          totalPosts += activePosts.length;
          activePosts.forEach(post => {
            if (post.type === 'event') totalEvents++;
            if (post.type === 'image') totalImages++;
            if (post.type === 'clip') totalVideos++;
          });
        }

        // Followers from shop document
        totalFollowers += (shop.followers || []).length;

        // Analytics aggregation
        if (shop.analytics) {
          totalRevenue += parseFloat(shop.analytics.totalRevenue || 0);
          totalTransactions += parseInt(shop.analytics.totalSales || 0);
          completedTransactions += parseInt(shop.analytics.completedSales || 0);
          pendingTransactions += parseInt(shop.analytics.pendingTransactions || 0);
          outstandingBalance += parseFloat(shop.analytics.outstandingBalance || 0);
          platformFeesEarned += parseFloat(shop.analytics.platformFeeEarned || 0);

          // Flag shops within $100 of next billing milestone
          const salesUntil = parseFloat(shop.analytics.salesUntilNextMilestone || 0);
          if (salesUntil > 0 && salesUntil <= 100) {
            shopsNearMilestone.push({
              id: shop.id,
              name: shop.name,
              salesUntil,
              nextFee: shop.analytics.nextMilestoneFee,
              nextThreshold: shop.analytics.nextMilestoneThreshold,
            });
          }
        }
      });

      // avgTransactionValue based on completed transactions only (accurate)
      const avgTransactionValue = completedTransactions > 0
        ? (totalRevenue / completedTransactions).toFixed(2)
        : '0.00';

      setStats({
        totalShops: shopsData.length,
        activeShops,
        pausedShops,
        deletedShops,
        totalItems,
        totalServices,
        totalUsers: shopsData.length,
        totalRevenue: totalRevenue.toFixed(2),
        totalTransactions,
        completedTransactions,
        pendingTransactions,
        avgTransactionValue,
        totalPosts,
        totalEvents,
        totalImages,
        totalVideos,
        stripeActiveCount,
        stripePendingCount,
        stripeIncompleteCount,
        outstandingBalance: outstandingBalance.toFixed(2),
        platformFeesEarned: platformFeesEarned.toFixed(2),
        totalFollowers,
        shopsNearMilestone,
      });

      // Sort by creation date
      const sortedShops = shopsData.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );

      setShops(sortedShops);

      // Store in module-level cache
      _dashboardCache = {
        shops: sortedShops,
        stats: {
          activeShops, pausedShops, deletedShops, totalItems, totalServices,
          totalUsers: shopsData.length, totalRevenue: totalRevenue.toFixed(2),
          totalTransactions, completedTransactions, pendingTransactions,
          avgTransactionValue, totalPosts, totalEvents, totalImages, totalVideos,
          stripeActiveCount, stripePendingCount, stripeIncompleteCount,
          outstandingBalance: outstandingBalance.toFixed(2),
          platformFeesEarned: platformFeesEarned.toFixed(2),
          totalFollowers, shopsNearMilestone,
          totalShops: shopsData.length,
        },
        pendingRequests: pendingRequestsData,
        cachedAt: Date.now(),
      };

      console.log('📊 Dashboard data loaded:', {
        shops: shopsData.length,
        totalRevenue: totalRevenue.toFixed(2),
        transactions: totalTransactions
      });

    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);
      showNotification('error', 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    _dashboardCache = null; // Invalidate cache
    setRefreshing(true);
    await fetchDashboardData(true);
    setTimeout(() => setRefreshing(false), 500);
    showNotification('success', 'Dashboard refreshed successfully');
  };

  useEffect(() => {
    fetchDashboardData();
    fetchPromoCodes();
  }, []);

  // Filter shops
  const filteredShops = shops.filter(shop => {
    const matchesSearch =
      shop.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shop.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shop.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shop.analytics?.ownerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shop.analytics?.ownerDisplayName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' || 
      shop.status === statusFilter;
    
    const matchesStripe = 
      stripeFilter === 'all' ||
      (stripeFilter === 'active' && shop.stripeReady) ||
      (stripeFilter === 'pending' && shop.stripeAccountStatus === 'pending_verification') ||
      (stripeFilter === 'incomplete' && shop.stripeAccountId && !shop.stripeReady);
    
    return matchesSearch && matchesStatus && matchesStripe;
  });

  // Notification helper
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Account Management Actions
  const handlePauseAccount = async () => {
    if (!selectedShop || processingAction) return;
    
    try {
      setProcessingAction(true);
      
      const response = await fetch('/api/process-billing-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'pause_account',
          userId: selectedShop.id
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to pause account');
      }
      
      showNotification('success', `Account "${selectedShop.name}" paused successfully`);
      setShowPauseModal(false);
      setSelectedShop(null);
      await fetchDashboardData();
      
    } catch (error) {
      console.error('Error pausing account:', error);
      showNotification('error', error.message);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleResumeAccount = async () => {
    if (!selectedShop || processingAction) return;
    
    try {
      setProcessingAction(true);
      
      const response = await fetch('/api/process-billing-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'resume_account',
          userId: selectedShop.id
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to resume account');
      }
      
      showNotification('success', `Account "${selectedShop.name}" resumed successfully`);
      setShowResumeModal(false);
      setSelectedShop(null);
      await fetchDashboardData();
      
    } catch (error) {
      console.error('Error resuming account:', error);
      showNotification('error', error.message);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedShop || processingAction) return;
    
    try {
      setProcessingAction(true);
      
      const response = await fetch('/api/process-billing-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'delete_account',
          userId: selectedShop.id
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        
        // Check for outstanding balance
        if (error.balance) {
          showNotification(
            'error', 
            `Cannot delete: Account has outstanding balance of $${error.balance.toFixed(2)}`
          );
          setShowDeleteModal(false);
          setSelectedShop(null);
          setProcessingAction(false);
          return;
        }
        
        throw new Error(error.error || 'Failed to delete account');
      }
      
      showNotification('success', `Account "${selectedShop.name}" deleted successfully`);
      setShowDeleteModal(false);
      setSelectedShop(null);
      await fetchDashboardData();
      
    } catch (error) {
      console.error('Error deleting account:', error);
      showNotification('error', error.message);
    } finally {
      setProcessingAction(false);
    }
  };

  // Export data
  const handleExportData = () => {
    const exportData = filteredShops.map(shop => ({
      'Shop Name': shop.name,
      'Username': shop.username,
      'Status': shop.status,
      'Total Items': (shop.items?.filter(i => !i.deleted).length || 0) + 
                     (shop.services?.filter(s => !s.deleted).length || 0),
      'Total Revenue': shop.analytics?.totalRevenue || '0.00',
      'Total Sales': shop.analytics?.totalSales || 0,
      'Completion Rate': shop.analytics?.completionRate || '0',
      'Stripe Status': shop.stripeReady ? 'Active' : 
                       shop.stripeAccountStatus || 'Not Connected',
      'Outstanding Balance': shop.analytics?.outstandingBalance || '0.00',
      'Created Date': new Date(shop.createdAt).toLocaleDateString()
    }));
    
    const csv = [
      Object.keys(exportData[0]).join(','),
      ...exportData.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kalkode-shops-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    showNotification('success', 'Data exported successfully');
  };

  const renderRequests = () => (
  <DataTable>
    <TableHeader>
      <h3>📩 Pending Account Requests ({pendingRequests.length})</h3>
      <TableControls>
        <ActionButton onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
          Refresh
        </ActionButton>
      </TableControls>
    </TableHeader>
    
    {pendingRequests.length > 0 ? (
      isMobile ? (
        // Mobile Card View
        <div style={{ padding: '1rem' }}>
          {pendingRequests.map(request => (
            <MobileCard key={request.id}>
              <div className="card-header">
                <div className="card-title">{request.shopName}</div>
                <Badge status={
                  request.requestType === 'delete_request' ? 'danger' :
                  request.requestType === 'pause_request' ? 'warning' : 'success'
                }>
                  {request.requestType === 'delete_request' && <Trash2 size={10} />}
                  {request.requestType === 'pause_request' && <Pause size={10} />}
                  {request.requestType === 'resume_request' && <Play size={10} />}
                  {request.requestType.replace('_request', '').toUpperCase()}
                </Badge>
              </div>
              
              <div className="card-body">
                <div className="card-item">
                  <span className="label">Username</span>
                  <span className="value">@{request.username}</span>
                </div>
                <div className="card-item">
                  <span className="label">Current Status</span>
                  <span className="value">
                    <Badge status={request.currentStatus}>
                      {request.currentStatus}
                    </Badge>
                  </span>
                </div>
                <div className="card-item">
                  <span className="label">Requested</span>
                  <span className="value">
                    {request.createdAt && new Date(request.createdAt.toDate()).toLocaleString()}
                  </span>
                </div>
              </div>
              
              <QuickActions style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <QuickActionButton
                  variant="danger"
                  onClick={() => {
                    setSelectedRequest(request);
                    processAccountRequest(request.id, 'reject');
                  }}
                  disabled={processingAction}
                >
                  <XCircle size={14} />
                  Reject
                </QuickActionButton>
                <QuickActionButton
                  variant="success"
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowRequestsModal(true);
                  }}
                  disabled={processingAction}
                >
                  <CheckCircle size={14} />
                  Review & Approve
                </QuickActionButton>
              </QuickActions>
            </MobileCard>
          ))}
        </div>
      ) : (
        // Desktop Table View
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Shop Name</th>
                <th>Request Type</th>
                <th>Current Status</th>
                <th>Requested</th>
                <th>User ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingRequests.map(request => (
                <tr 
                  key={request.id}
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowRequestsModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <strong>{request.shopName}</strong>
                    <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.25rem' }}>
                      @{request.username}
                    </div>
                  </td>
                  <td>
                    <Badge status={
                      request.requestType === 'delete_request' ? 'danger' :
                      request.requestType === 'pause_request' ? 'warning' : 'success'
                    }>
                      {request.requestType === 'delete_request' && <Trash2 size={12} />}
                      {request.requestType === 'pause_request' && <Pause size={12} />}
                      {request.requestType === 'resume_request' && <Play size={12} />}
                      {request.requestType.replace('_request', '').replace('_', ' ').toUpperCase()}
                    </Badge>
                  </td>
                  <td>
                    <Badge status={request.currentStatus || 'active'}>
                      {request.currentStatus || 'active'}
                    </Badge>
                  </td>
                  <td>
                    <div>
                      {request.createdAt && new Date(request.createdAt.toDate()).toLocaleDateString()}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.25rem' }}>
                      {request.createdAt && new Date(request.createdAt.toDate()).toLocaleTimeString()}
                    </div>
                  </td>
                  <td>
                    <code style={{ 
                      fontSize: '0.75rem', 
                      background: 'rgba(255,255,255,0.1)', 
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px'
                    }}>
                      {request.userId.substring(0, 8)}...
                    </code>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                      <QuickActionButton
                        variant="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRequest(request);
                          processAccountRequest(request.id, 'reject');
                        }}
                        disabled={processingAction}
                      >
                        <XCircle size={14} />
                        Reject
                      </QuickActionButton>
                      <QuickActionButton
                        variant="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRequest(request);
                          setShowRequestsModal(true);
                        }}
                        disabled={processingAction}
                      >
                        <CheckCircle size={14} />
                        Review
                      </QuickActionButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )
    ) : (
      <EmptyState>
        <Bell />
        <p>No pending account requests</p>
        <p style={{ fontSize: '0.9rem', opacity: 0.6, marginTop: '0.5rem' }}>
          All user requests have been processed
        </p>
      </EmptyState>
    )}
  </DataTable>
);

  // Render functions
  const renderOverview = () => (
    <>
      <StatsGrid>
        <StatCard trend="up" iconBg="rgba(76, 175, 80, 0.2)" iconColor="#4CAF50">
          <div className="stat-header">
            <div>
              <div className="stat-value">{stats.totalShops}</div>
              <div className="stat-label">Total Shops</div>
            </div>
            <div className="stat-icon">
              <Store size={24} />
            </div>
          </div>
          <div className="stat-change">
            <CheckCircle size={16} />
            <span>{stats.activeShops} Active</span>
          </div>
        </StatCard>

        <StatCard trend="up" iconBg="rgba(33, 150, 243, 0.2)" iconColor="#2196F3">
          <div className="stat-header">
            <div>
              <div className="stat-value">${stats.totalRevenue}</div>
              <div className="stat-label">Seller Transaction Volume</div>
            </div>
            <div className="stat-icon">
              <DollarSign size={24} />
            </div>
          </div>
          <div className="stat-change">
            <TrendingUp size={16} />
            <span>{stats.completedTransactions} Completed · {stats.pendingTransactions} Pending</span>
          </div>
        </StatCard>

        <StatCard trend="up" iconBg="rgba(0, 200, 83, 0.2)" iconColor="#00C853">
          <div className="stat-header">
            <div>
              <div className="stat-value">${stats.platformFeesEarned}</div>
              <div className="stat-label">Platform Fees Earned</div>
            </div>
            <div className="stat-icon">
              <TrendingUp size={24} />
            </div>
          </div>
          <div className="stat-change">
            <BarChart2 size={16} />
            <span>4.1% of seller volume</span>
          </div>
        </StatCard>

        <StatCard iconBg="rgba(156, 39, 176, 0.2)" iconColor="#9C27B0">
          <div className="stat-header">
            <div>
              <div className="stat-value">${stats.avgTransactionValue}</div>
              <div className="stat-label">Avg Order Value</div>
            </div>
            <div className="stat-icon">
              <Activity size={24} />
            </div>
          </div>
          <div className="stat-change">
            <BarChart2 size={16} />
            <span>Completed orders only</span>
          </div>
        </StatCard>

        <StatCard trend="up" iconBg="rgba(76, 175, 80, 0.2)" iconColor="#4CAF50">
          <div className="stat-header">
            <div>
              <div className="stat-value">{stats.stripeActiveCount}</div>
              <div className="stat-label">Stripe Connected</div>
            </div>
            <div className="stat-icon">
              <CreditCard size={24} />
            </div>
          </div>
          <div className="stat-change">
            <Clock size={16} />
            <span>{stats.stripePendingCount} Pending</span>
          </div>
        </StatCard>

        <StatCard iconBg="rgba(255, 152, 0, 0.2)" iconColor="#FF9800">
          <div className="stat-header">
            <div>
              <div className="stat-value">{stats.totalItems}</div>
              <div className="stat-label">Total Products</div>
            </div>
            <div className="stat-icon">
              <Package size={24} />
            </div>
          </div>
          <div className="stat-change">
            <ShoppingBag size={16} />
            <span>{stats.totalServices} Services</span>
          </div>
        </StatCard>

        <StatCard 
          trend={stats.outstandingBalance > 0 ? 'down' : 'up'}
          iconBg={stats.outstandingBalance > 0 ? "rgba(244, 67, 54, 0.2)" : "rgba(76, 175, 80, 0.2)"}
          iconColor={stats.outstandingBalance > 0 ? "#F44336" : "#4CAF50"}
        >
          <div className="stat-header">
            <div>
              <div className="stat-value">${stats.outstandingBalance}</div>
              <div className="stat-label">Outstanding Balance</div>
            </div>
            <div className="stat-icon">
              <AlertCircle size={24} />
            </div>
          </div>
          <div className="stat-change">
            {stats.outstandingBalance > 0 ? (
              <>
                <AlertTriangle size={16} />
                <span>Needs Attention</span>
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                <span>All Clear</span>
              </>
            )}
          </div>
        </StatCard>

        <StatCard iconBg="rgba(233, 30, 99, 0.2)" iconColor="#E91E63">
          <div className="stat-header">
            <div>
              <div className="stat-value">{stats.totalPosts}</div>
              <div className="stat-label">Community Posts</div>
            </div>
            <div className="stat-icon">
              <Activity size={24} />
            </div>
          </div>
          <div className="stat-change">
            <ImageIcon size={16} />
            <span>{stats.totalEvents} Events · {stats.totalImages} Images · {stats.totalVideos} Clips</span>
          </div>
        </StatCard>

        <StatCard trend="up" iconBg="rgba(3, 169, 244, 0.2)" iconColor="#03A9F4">
          <div className="stat-header">
            <div>
              <div className="stat-value">{stats.totalFollowers}</div>
              <div className="stat-label">Total Followers</div>
            </div>
            <div className="stat-icon">
              <Users size={24} />
            </div>
          </div>
          <div className="stat-change">
            <TrendingUp size={16} />
            <span>Across all shops</span>
          </div>
        </StatCard>

        <StatCard iconBg="rgba(255, 152, 0, 0.2)" iconColor="#FF9800">
          <div className="stat-header">
            <div>
              <div className="stat-value">{stats.pausedShops}</div>
              <div className="stat-label">Paused Accounts</div>
            </div>
            <div className="stat-icon">
              <Pause size={24} />
            </div>
          </div>
          <div className="stat-change">
            <Trash2 size={16} />
            <span>{stats.deletedShops} Deleted</span>
          </div>
        </StatCard>
      </StatsGrid>

      {/* Stripe Mode Toggle */}
      <DataTable style={{ marginBottom: '1.5rem' }}>
        <TableHeader>
          <h3>💳 Stripe Payment Mode</h3>
        </TableHeader>
        <div style={{ padding: '1.5rem' }}>
          {stripeMode === 'test' && (
            <div style={{
              background: 'rgba(255, 193, 7, 0.15)',
              border: '1px solid rgba(255, 193, 7, 0.5)',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#FFC107',
              fontSize: '0.9rem',
            }}>
              <AlertTriangle size={16} />
              <strong>TEST MODE ACTIVE</strong> — Payments are using Stripe test keys. No real money is processed.
            </div>
          )}
          <StripeModeRow>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ color: stripeMode === 'live' ? '#4CAF50' : 'rgba(255,255,255,0.4)', fontWeight: stripeMode === 'live' ? 700 : 400 }}>
                LIVE
              </span>
              <button
                onClick={handleStripeModeToggle}
                disabled={stripeModeLoading}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  border: 'none',
                  cursor: stripeModeLoading ? 'not-allowed' : 'pointer',
                  background: stripeMode === 'test' ? '#FFC107' : '#4CAF50',
                  position: 'relative',
                  transition: 'background 0.2s',
                  opacity: stripeModeLoading ? 0.6 : 1,
                }}
              >
                <span style={{
                  position: 'absolute',
                  top: '3px',
                  left: stripeMode === 'test' ? '27px' : '3px',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'left 0.2s',
                }} />
              </button>
              <span style={{ color: stripeMode === 'test' ? '#FFC107' : 'rgba(255,255,255,0.4)', fontWeight: stripeMode === 'test' ? 700 : 400 }}>
                TEST
              </span>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
              Currently: <strong style={{ color: stripeMode === 'live' ? '#4CAF50' : '#FFC107' }}>{stripeMode.toUpperCase()}</strong>
              {' '}— Toggle to switch between live and test Stripe API keys across the entire platform.
            </span>
          </StripeModeRow>
        </div>
      </DataTable>

      {/* Quick Stats Summary */}
      <DataTable>
        <TableHeader>
          <h3>📊 Platform Health</h3>
        </TableHeader>
        <SectionPad>
          <ResponsiveGrid $min="220px" $gap="1.5rem">
            <div>
              <h4 style={{ color: '#800000', marginBottom: '1rem' }}>Account Status</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Active Shops:</span>
                  <strong>{stats.activeShops}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Paused Shops:</span>
                  <strong>{stats.pausedShops}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Deleted Shops:</span>
                  <strong>{stats.deletedShops}</strong>
                </div>
              </div>
            </div>

            <div>
              <h4 style={{ color: '#800000', marginBottom: '1rem' }}>Inventory</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Products:</span>
                  <strong>{stats.totalItems}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Services:</span>
                  <strong>{stats.totalServices}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Avg per Shop:</span>
                  <strong>{((stats.totalItems + stats.totalServices) / (stats.totalShops || 1)).toFixed(1)}</strong>
                </div>
              </div>
            </div>

            <div>
              <h4 style={{ color: '#800000', marginBottom: '1rem' }}>Revenue & Fees</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Seller Volume:</span>
                  <strong>${stats.totalRevenue}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Platform Fees (4.1%):</span>
                  <strong style={{ color: '#00C853' }}>${stats.platformFeesEarned}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Seller Payouts (93%):</span>
                  <strong>${(parseFloat(stats.totalRevenue) * 0.93).toFixed(2)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Completed Orders:</span>
                  <strong>{stats.completedTransactions}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Pending Orders:</span>
                  <strong style={{ color: stats.pendingTransactions > 0 ? '#FFC107' : 'inherit' }}>
                    {stats.pendingTransactions}
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Avg Order Value:</span>
                  <strong>${stats.avgTransactionValue}</strong>
                </div>
              </div>
            </div>

            <div>
              <h4 style={{ color: '#800000', marginBottom: '1rem' }}>Social</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Followers:</span>
                  <strong>{stats.totalFollowers}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Community Posts:</span>
                  <strong>{stats.totalPosts}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Events:</span>
                  <strong>{stats.totalEvents}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Avg Followers/Shop:</span>
                  <strong>{((stats.totalFollowers) / (stats.activeShops || 1)).toFixed(1)}</strong>
                </div>
              </div>
            </div>

            <div>
              <h4 style={{ color: '#800000', marginBottom: '1rem' }}>Stripe Status</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Active:</span>
                  <strong style={{ color: '#4CAF50' }}>{stats.stripeActiveCount}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Pending Verification:</span>
                  <strong style={{ color: '#FFC107' }}>{stats.stripePendingCount}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Incomplete:</span>
                  <strong style={{ color: '#FF9800' }}>{stats.stripeIncompleteCount}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Not Connected:</span>
                  <strong style={{ color: '#F44336' }}>
                    {stats.totalShops - stats.stripeActiveCount - stats.stripePendingCount - stats.stripeIncompleteCount}
                  </strong>
                </div>
              </div>
            </div>
          </ResponsiveGrid>

          {/* Milestone Alerts */}
          {stats.shopsNearMilestone?.length > 0 && (
            <div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.5rem' }}>
              <h4 style={{ color: '#FFC107', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle size={16} /> Shops Near Next Billing Milestone
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {stats.shopsNearMilestone.map(s => {
                  const fullShop = shops.find(sh => sh.id === s.id);
                  return (
                    <MilestoneRow key={s.id}>
                      <span><strong>{s.name}</strong></span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ color: '#FFC107' }}>
                          ${s.salesUntil} away from ${s.nextFee} fee (at ${s.nextThreshold} sales)
                        </span>
                        {fullShop && (
                          <button
                            onClick={() => { setSelectedShop(fullShop); setBalanceMode('add'); setBalanceOverride(''); setShowBalanceModal(true); }}
                            style={{ background: 'rgba(255,193,7,0.2)', border: '1px solid rgba(255,193,7,0.5)', color: '#FFC107', borderRadius: '6px', padding: '0.2rem 0.6rem', fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            <DollarSign size={11} style={{ marginRight: 3 }} /> Manage Balance
                          </button>
                        )}
                      </div>
                    </MilestoneRow>
                  );
                })}
              </div>
            </div>
          )}

          {/* Balances Due */}
          {(() => {
            const due = shops.filter(sh => parseFloat(sh.analytics?.outstandingBalance || 0) > 0);
            if (!due.length) return null;
            return (
              <div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.5rem' }}>
                <h4 style={{ color: '#F44336', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <DollarSign size={16} /> Outstanding Balances Due ({due.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {due.map(sh => (
                    <MilestoneRow key={sh.id} style={{ background: 'rgba(244,67,54,0.08)', borderLeft: '3px solid rgba(244,67,54,0.5)' }}>
                      <span><strong>{sh.name}</strong></span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ color: '#F44336', fontWeight: 600 }}>
                          ${parseFloat(sh.analytics.outstandingBalance).toFixed(2)} due
                        </span>
                        <button
                          onClick={() => { setSelectedShop(sh); setBalanceMode('add'); setBalanceOverride(''); setShowBalanceModal(true); }}
                          style={{ background: 'rgba(244,67,54,0.2)', border: '1px solid rgba(244,67,54,0.5)', color: '#F44336', borderRadius: '6px', padding: '0.2rem 0.6rem', fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          <DollarSign size={11} style={{ marginRight: 3 }} /> Manage Balance
                        </button>
                      </div>
                    </MilestoneRow>
                  ))}
                </div>
              </div>
            );
          })()}
        </SectionPad>
      </DataTable>
    </>
  );

  const renderShops = () => (
    <DataTable>
      <TableHeader>
        <h3>All Shops ({filteredShops.length})</h3>
        <TableControls>
          <SearchInput
            type="text"
            placeholder="Search by name, email, username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="deleted">Deleted</option>
          </FilterSelect>
          <FilterSelect
            value={stripeFilter}
            onChange={(e) => setStripeFilter(e.target.value)}
          >
            <option value="all">All Stripe</option>
            <option value="active">Stripe Active</option>
            <option value="pending">Stripe Pending</option>
            <option value="incomplete">Stripe Incomplete</option>
          </FilterSelect>
          <ActionButton onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
          </ActionButton>
          <ActionButton onClick={handleExportData}>
            <Download size={16} />
            Export
          </ActionButton>
        </TableControls>
      </TableHeader>
      
      <ShopCardGrid>
        {filteredShops.map(shop => {
          const itemCount = shop.items?.filter(i => !i.deleted).length || 0;
          const serviceCount = shop.services?.filter(s => !s.deleted).length || 0;
          const totalCount = itemCount + serviceCount;
          const hasBalance = shop.analytics?.outstandingBalance > 0;
          const isPaused = shop.status === 'paused';
          const initials = (shop.name || '?').charAt(0).toUpperCase();
          const profileImg = typeof shop.profile === 'string' ? shop.profile : shop.profile?.image || null;

          return (
            <ShopCardItem
              key={shop.id}
              $hasBalance={hasBalance}
              $paused={isPaused}
              onClick={() => setDetailShop(shop)}
            >
              <div className="card-top">
                <div className="avatar">
                  {profileImg
                    ? <img src={profileImg} alt={shop.name} />
                    : initials}
                </div>
                <div className="info">
                  <div className="name">{shop.name || 'Unnamed Shop'}</div>
                  <div className="username">@{shop.username || 'N/A'}</div>
                  {shop.analytics?.ownerEmail && (
                    <div className="email">{shop.analytics.ownerEmail}</div>
                  )}
                </div>
                <div className="badges">
                  <Badge status={shop.status || 'active'} style={{ fontSize: '0.65rem', padding: '0.2rem 0.45rem' }}>
                    {shop.status === 'active' && <CheckCircle size={10} />}
                    {shop.status === 'paused' && <Pause size={10} />}
                    {shop.status === 'deleted' && <XCircle size={10} />}
                    {shop.status || 'active'}
                  </Badge>
                  {hasBalance && (
                    <Badge status="warning" style={{ fontSize: '0.65rem', padding: '0.2rem 0.45rem' }}>
                      <AlertTriangle size={10} />
                      Due
                    </Badge>
                  )}
                </div>
              </div>

              <div className="stats-row">
                <div className="stat-cell">
                  <div className="val">${shop.analytics?.totalRevenue || '0'}</div>
                  <div className="lbl">Revenue</div>
                </div>
                <div className="stat-cell">
                  <div className="val">{totalCount}</div>
                  <div className="lbl">Items</div>
                </div>
                <div className="stat-cell">
                  <div className="val">{shop.analytics?.completedSales || 0}</div>
                  <div className="lbl">Sales</div>
                </div>
                <div className="stat-cell">
                  <div className="val" style={{ color: hasBalance ? '#F44336' : '#4CAF50' }}>
                    {hasBalance ? `$${parseFloat(shop.analytics.outstandingBalance).toFixed(2)}` : '$0'}
                  </div>
                  <div className="lbl">Due</div>
                </div>
              </div>

              <div className="card-footer">
                <span style={{ textTransform: 'capitalize' }}>
                  {shop.storeType || 'general'}
                </span>
                <span>
                  {shop.stripeReady ? '✅ Stripe' : shop.stripeAccountStatus === 'pending_verification' ? '⏳ Stripe' : '⚠️ No Stripe'}
                </span>
                <span>
                  {shop.createdAt ? new Date(shop.createdAt).toLocaleDateString() : '—'}
                </span>
              </div>
            </ShopCardItem>
          );
        })}
      </ShopCardGrid>
      
      {filteredShops.length === 0 && (
        <EmptyState>
          <Search />
          <p>No shops found matching your filters</p>
        </EmptyState>
      )}
    </DataTable>
  );

  const renderAnalytics = () => (
    <>
      <DataTable>
        <TableHeader>
          <h3>📈 Platform Analytics</h3>
        </TableHeader>
        <SectionPad>
          <ResponsiveGrid $min="240px" $gap="1.5rem">
            <div>
              <h4 style={{ color: '#800000', marginBottom: '1rem' }}>Revenue Breakdown</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '6px',
                  border: '1px solid rgba(76, 175, 80, 0.2)'
                }}>
                  <span>Total Platform Revenue:</span>
                  <strong style={{ color: '#4CAF50' }}>${stats.totalRevenue}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Transactions:</span>
                  <strong>{stats.totalTransactions}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Average Order Value:</span>
                  <strong>${stats.avgTransactionValue}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Revenue per Shop:</span>
                  <strong>${(parseFloat(stats.totalRevenue) / (stats.totalShops || 1)).toFixed(2)}</strong>
                </div>
              </div>
            </div>

            <div>
              <h4 style={{ color: '#800000', marginBottom: '1rem' }}>Shop Metrics</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Active Shops:</span>
                  <strong>{stats.activeShops} ({((stats.activeShops / stats.totalShops) * 100).toFixed(1)}%)</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Avg Items per Shop:</span>
                  <strong>{((stats.totalItems + stats.totalServices) / (stats.totalShops || 1)).toFixed(1)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Avg Posts per Shop:</span>
                  <strong>{(stats.totalPosts / (stats.totalShops || 1)).toFixed(1)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Stripe Adoption:</span>
                  <strong>{((stats.stripeActiveCount / stats.totalShops) * 100).toFixed(1)}%</strong>
                </div>
              </div>
            </div>

            <div>
              <h4 style={{ color: '#800000', marginBottom: '1rem' }}>Content Distribution</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Products:</span>
                  <strong>{stats.totalItems}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Services:</span>
                  <strong>{stats.totalServices}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Community Posts:</span>
                  <strong>{stats.totalPosts}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Media Files:</span>
                  <strong>{stats.totalImages + stats.totalVideos}</strong>
                </div>
              </div>
            </div>

            <div>
              <h4 style={{ color: '#800000', marginBottom: '1rem' }}>Health Indicators</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: stats.outstandingBalance > 0 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '6px',
                  border: `1px solid ${stats.outstandingBalance > 0 ? 'rgba(244, 67, 54, 0.2)' : 'rgba(76, 175, 80, 0.2)'}`
                }}>
                  <span>Outstanding Balance:</span>
                  <strong style={{ color: stats.outstandingBalance > 0 ? '#F44336' : '#4CAF50' }}>
                    ${stats.outstandingBalance}
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Paused Accounts:</span>
                  <strong>{stats.pausedShops}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Deleted Accounts:</span>
                  <strong>{stats.deletedShops}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Stripe Incomplete:</span>
                  <strong>{stats.stripeIncompleteCount + stats.stripePendingCount}</strong>
                </div>
              </div>
            </div>
          </ResponsiveGrid>
        </SectionPad>
      </DataTable>

      {/* Top Performers */}
      <DataTable>
        <TableHeader>
          <h3>🏆 Top Performing Shops</h3>
        </TableHeader>
        {isMobile ? (
          <div style={{ padding: '0.5rem 0' }}>
            {shops
              .filter(shop => shop.analytics?.totalRevenue > 0)
              .sort((a, b) => parseFloat(b.analytics?.totalRevenue || 0) - parseFloat(a.analytics?.totalRevenue || 0))
              .slice(0, 10)
              .map((shop, index) => (
                <TopPerformerCard key={shop.id}>
                  <div className="rank">
                    <Badge status={index < 3 ? 'active' : 'default'}>#{index + 1}</Badge>
                  </div>
                  <div className="shop-info">
                    <div className="shop-name">{shop.name}</div>
                    <div className="shop-handle">@{shop.username} · {shop.analytics?.completionRate || 0}% complete</div>
                  </div>
                  <div className="stats-pill">
                    <div className="revenue">${shop.analytics?.totalRevenue || '0.00'}</div>
                    <div className="sub">{shop.analytics?.completedSales || 0} sales · avg ${shop.analytics?.avgOrderValue || '0.00'}</div>
                  </div>
                </TopPerformerCard>
              ))}
          </div>
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Shop Name</th>
                  <th>Revenue</th>
                  <th>Sales</th>
                  <th>Avg Order</th>
                  <th>Completion Rate</th>
                </tr>
              </thead>
              <tbody>
                {shops
                  .filter(shop => shop.analytics?.totalRevenue > 0)
                  .sort((a, b) => parseFloat(b.analytics?.totalRevenue || 0) - parseFloat(a.analytics?.totalRevenue || 0))
                  .slice(0, 10)
                  .map((shop, index) => (
                    <tr key={shop.id}>
                      <td>
                        <Badge status={index < 3 ? 'active' : 'default'}>
                          #{index + 1}
                        </Badge>
                      </td>
                      <td>
                        <strong>{shop.name}</strong>
                        <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>@{shop.username}</div>
                      </td>
                      <td><strong>${shop.analytics?.totalRevenue || '0.00'}</strong></td>
                      <td>{shop.analytics?.completedSales || 0}</td>
                      <td>${shop.analytics?.avgOrderValue || '0.00'}</td>
                      <td>{shop.analytics?.completionRate || 0}%</td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </DataTable>
    </>
  );

  if (loading) {
  return (
    <DashboardContainer>
      <PageLoadingSpinner>
        <div className="spinner" />
        <p>Loading dashboard data...</p>
      </PageLoadingSpinner>
    </DashboardContainer>
  );
}

return (
  <DashboardContainer>
    <DashboardHeader>
      <HeaderContent>
        <Title>Admin Dashboard</Title>
        <HeaderActions>
          {/* Pending Requests Indicator */}
          {pendingRequests.length > 0 && (
            <ActionButton 
              onClick={() => setActiveTab('requests')}
              style={{ position: 'relative' }}
            >
              <Bell size={18} />
              Requests
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: '#FF4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                fontSize: '0.7rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {pendingRequests.length}
              </span>
            </ActionButton>
          )}
          {_dashboardCache && (
            <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
              cached {Math.round((Date.now() - _dashboardCache.cachedAt) / 60000)}m ago
            </span>
          )}
          <ActionButton onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={18} className={refreshing ? 'spinning' : ''} />
            {refreshing ? 'Updating...' : 'Refresh'}
          </ActionButton>
          <ActionButton primary onClick={() => { setActiveTab('myshop'); fetchMyShop(); }}>
            <Store size={18} />
            My Shop
          </ActionButton>
        </HeaderActions>
      </HeaderContent>
    </DashboardHeader>

    <DashboardContent>
      {notification && (
        <NotificationBanner type={notification.type}>
          <div className="message">
            {notification.type === 'success' && <CheckCircle size={20} />}
            {notification.type === 'error' && <XCircle size={20} />}
            {notification.type === 'warning' && <AlertTriangle size={20} />}
            {notification.type === 'info' && <Bell size={20} />}
            <span>{notification.message}</span>
          </div>
          <button onClick={() => setNotification(null)}>×</button>
        </NotificationBanner>
      )}

      <TabBar>
        <Tab 
          active={activeTab === 'overview'} 
          onClick={() => setActiveTab('overview')}
        >
          <BarChart2 size={18} />
          Overview
        </Tab>
        <Tab 
          active={activeTab === 'shops'} 
          onClick={() => setActiveTab('shops')}
        >
          <Store size={18} />
          All Shops
        </Tab>
        <Tab 
          active={activeTab === 'analytics'} 
          onClick={() => setActiveTab('analytics')}
        >
          <TrendingUp size={18} />
          Analytics
        </Tab>
        <Tab 
          active={activeTab === 'requests'} 
          onClick={() => setActiveTab('requests')}
        >
          <Bell size={18} />
          Requests
          {pendingRequests.length > 0 && (
            <Badge status="warning" style={{ marginLeft: '0.5rem' }}>
              {pendingRequests.length}
            </Badge>
          )}
        </Tab>
        <Tab
          active={activeTab === 'promos'}
          onClick={() => setActiveTab('promos')}
        >
          <DollarSign size={18} />
          Promo Codes
        </Tab>
        <Tab
          active={activeTab === 'myshop'}
          onClick={() => { setActiveTab('myshop'); fetchMyShop(); }}
        >
          <Store size={18} />
          My Shop
        </Tab>
      </TabBar>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'shops' && renderShops()}
      {activeTab === 'analytics' && renderAnalytics()}
      {activeTab === 'requests' && renderRequests()}
      {activeTab === 'promos' && renderPromoCodes()}
      {activeTab === 'myshop' && renderMyShop()}
    </DashboardContent>

    {/* Shop Detail Drawer */}
    {detailShop && (() => {
      const shop = detailShop;
      const itemCount = shop.items?.filter(i => !i.deleted).length || 0;
      const serviceCount = shop.services?.filter(s => !s.deleted).length || 0;
      const totalCount = itemCount + serviceCount;
      return (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 499 }}
            onClick={() => setDetailShop(null)}
          />
          <DetailDrawer>
            <div className="drawer-header">
              <div className="drawer-title">
                <div className="name">{shop.name || 'Unnamed Shop'}</div>
                <div className="sub">@{shop.username || 'N/A'} · {shop.storeType || 'general'}</div>
              </div>
              <button className="close-btn" onClick={() => setDetailShop(null)}>
                <X size={16} />
              </button>
            </div>

            <div className="drawer-body">
              <DrawerSection>
                <div className="section-title"><Users size={13} />Owner</div>
                <div className="section-rows">
                  <div className="row"><span className="k">Name</span><span className="v">{shop.analytics?.ownerDisplayName || '—'}</span></div>
                  <div className="row"><span className="k">Email</span><span className="v">{shop.analytics?.ownerEmail ? <a href={`mailto:${shop.analytics.ownerEmail}`} style={{ color: '#03A9F4', textDecoration: 'none' }}>{shop.analytics.ownerEmail}</a> : '—'}</span></div>
                  <div className="row"><span className="k">Phone</span><span className="v">{shop.analytics?.ownerPhone || shop.phone || '—'}</span></div>
                  <div className="row"><span className="k">UID</span><span className="v" style={{ fontFamily: 'monospace', fontSize: '0.72rem', opacity: 0.7 }}>{shop.id}</span></div>
                </div>
              </DrawerSection>

              <DrawerSection>
                <div className="section-title"><Store size={13} />Shop Details</div>
                <div className="section-rows">
                  <div className="row"><span className="k">Store Type</span><span className="v" style={{ textTransform: 'capitalize' }}>{shop.storeType || 'general'}</span></div>
                  <div className="row"><span className="k">City</span><span className="v">{shop.city || '—'}</span></div>
                  <div className="row"><span className="k">Member Since</span><span className="v">{shop.createdAt ? new Date(shop.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</span></div>
                  <div className="row"><span className="k">Last Updated</span><span className="v">{shop.updatedAt ? new Date(shop.updatedAt).toLocaleDateString() : '—'}</span></div>
                  {shop.description && <div className="row"><span className="k">Description</span><span className="v" style={{ fontSize: '0.78rem' }}>{shop.description.length > 100 ? shop.description.slice(0, 100) + '…' : shop.description}</span></div>}
                </div>
              </DrawerSection>

              <DrawerSection>
                <div className="section-title"><DollarSign size={13} />Financials</div>
                <div className="section-rows">
                  <div className="row"><span className="k">Total Revenue</span><span className="v" style={{ color: '#4CAF50' }}>${shop.analytics?.totalRevenue || '0.00'}</span></div>
                  <div className="row"><span className="k">Avg Order Value</span><span className="v">${shop.analytics?.avgOrderValue || '0.00'}</span></div>
                  <div className="row"><span className="k">Completed Sales</span><span className="v">{shop.analytics?.completedSales || 0}</span></div>
                  <div className="row"><span className="k">Completion Rate</span><span className="v">{shop.analytics?.completionRate || 0}%</span></div>
                  <div className="row"><span className="k">Platform Fee Earned</span><span className="v" style={{ color: '#00C853' }}>${shop.analytics?.platformFeeEarned || '0.00'}</span></div>
                  <div className="row"><span className="k">Outstanding Balance</span><span className="v" style={{ color: parseFloat(shop.analytics?.outstandingBalance) > 0 ? '#FF4444' : '#4CAF50' }}>${shop.analytics?.outstandingBalance || '0.00'}</span></div>
                </div>
              </DrawerSection>

              <DrawerSection>
                <div className="section-title"><Package size={13} />Inventory & Social</div>
                <div className="section-rows">
                  <div className="row"><span className="k">Products</span><span className="v">{itemCount}</span></div>
                  <div className="row"><span className="k">Services</span><span className="v">{serviceCount}</span></div>
                  <div className="row"><span className="k">Total Items</span><span className="v">{totalCount}</span></div>
                  <div className="row"><span className="k">Followers</span><span className="v">{(shop.followers || []).length}</span></div>
                  <div className="row"><span className="k">Following</span><span className="v">{(shop.following || []).length}</span></div>
                  <div className="row"><span className="k">Community Posts</span><span className="v">{shop.analytics?.postCount ?? (shop.communityPosts?.filter(p => !p.deleted).length || 0)}</span></div>
                  <div className="row"><span className="k">Pending Orders</span><span className="v" style={{ color: shop.analytics?.pendingTransactions > 0 ? '#FFC107' : 'inherit' }}>{shop.analytics?.pendingTransactions || 0}</span></div>
                </div>
              </DrawerSection>

              <DrawerSection>
                <div className="section-title"><CreditCard size={13} />Stripe</div>
                <div className="section-rows">
                  <div className="row"><span className="k">Status</span><span className="v">{shop.stripeReady ? '✅ Active' : shop.stripeAccountStatus === 'pending_verification' ? '⏳ Pending' : shop.stripeAccountId ? '⚠️ Incomplete' : '❌ Not Connected'}</span></div>
                  <div className="row"><span className="k">Billing Sales</span><span className="v">${shop.analytics?.billingTotalSales || '0.00'}</span></div>
                  <div className="row"><span className="k">Next Milestone</span><span className="v">{shop.analytics?.nextMilestoneThreshold ? `$${shop.analytics.nextMilestoneFee} fee at $${shop.analytics.nextMilestoneThreshold}` : '—'}</span></div>
                </div>
              </DrawerSection>

              <DrawerSection>
                <div className="section-title"><Settings size={13} />Actions</div>
                <div style={{ padding: '0.75rem 0.85rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <QuickActionButton onClick={() => { setDetailShop(null); window.open(`/${shop.username}`, '_blank'); }}>
                    <Eye size={15} />View Shop
                  </QuickActionButton>
                  {shop.status === 'active' && (
                    <QuickActionButton variant="warning" onClick={() => { setSelectedShop(shop); setDetailShop(null); setShowPauseModal(true); }}>
                      <Pause size={15} />Pause Account
                    </QuickActionButton>
                  )}
                  {shop.status === 'paused' && (
                    <QuickActionButton variant="success" onClick={() => { setSelectedShop(shop); setDetailShop(null); setShowResumeModal(true); }}>
                      <Play size={15} />Resume Account
                    </QuickActionButton>
                  )}
                  <QuickActionButton variant="warning" onClick={() => { setSelectedShop(shop); setDetailShop(null); setBalanceMode('add'); setBalanceOverride(''); setShowBalanceModal(true); }}>
                    <DollarSign size={15} />Manage Balance
                  </QuickActionButton>
                  <QuickActionButton variant="danger" disabled={shop.status === 'deleted'} onClick={() => { setSelectedShop(shop); setDetailShop(null); setShowDeleteModal(true); }}>
                    <Trash2 size={15} />Delete Account
                  </QuickActionButton>
                </div>
              </DrawerSection>
            </div>
          </DetailDrawer>
        </>
      );
    })()}

    {/* Pause Account Modal */}
    {showPauseModal && selectedShop && (
      <ModalOverlay onClick={() => !processingAction && setShowPauseModal(false)}>
        <ModalContent variant="warning" onClick={(e) => e.stopPropagation()}>
          <h3>
            <Pause size={24} />
            Pause Account?
          </h3>
          <p>
            This will pause the account for <strong>{selectedShop.name}</strong>.
            All items and services will be hidden from the marketplace until the account is resumed.
          </p>
          <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
            The shop owner will still have access to their dashboard and can resume their account at any time.
          </p>
          <div className="modal-actions">
            <button 
              className="modal-button cancel"
              onClick={() => setShowPauseModal(false)}
              disabled={processingAction}
            >
              Cancel
            </button>
            <button 
              className="modal-button confirm"
              onClick={handlePauseAccount}
              disabled={processingAction}
            >
              {processingAction ? <LoadingSpinner /> : 'Pause Account'}
            </button>
          </div>
        </ModalContent>
      </ModalOverlay>
    )}

    {/* Balance Override Modal */}
{showBalanceModal && selectedShop && (() => {
  const currentBalance = parseFloat(selectedShop.analytics?.outstandingBalance || 0);
  const inputAmt = parseFloat(balanceOverride) || 0;
  const resultBalance = balanceMode === 'add'
    ? parseFloat((currentBalance + inputAmt).toFixed(2))
    : inputAmt;
  const isClearing = resultBalance === 0;
  const isAdding = balanceMode === 'add' && inputAmt > 0;

  return (
    <ModalOverlay onClick={() => !processingAction && setShowBalanceModal(false)}>
      <ModalContent variant="warning" onClick={e => e.stopPropagation()}>
        <h3><DollarSign size={24} />Manage Balance — {selectedShop.name}</h3>

        {/* Current balance pill */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: currentBalance > 0 ? 'rgba(244,67,54,0.12)' : 'rgba(76,175,80,0.12)',
          border: `1px solid ${currentBalance > 0 ? 'rgba(244,67,54,0.3)' : 'rgba(76,175,80,0.3)'}`,
          borderRadius: '8px', padding: '0.65rem 1rem', marginBottom: '1.25rem',
        }}>
          <span style={{ fontSize: '0.85rem', opacity: 0.75 }}>Current Balance Due</span>
          <strong style={{ fontSize: '1.1rem', color: currentBalance > 0 ? '#F44336' : '#4CAF50' }}>
            ${currentBalance.toFixed(2)}
          </strong>
        </div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          {[['add', '+ Add Amount'], ['set', '✎ Set Exact']].map(([mode, label]) => (
            <button key={mode} onClick={() => { setBalanceMode(mode); setBalanceOverride(''); }} style={{
              flex: 1, padding: '0.55rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: '0.85rem', transition: 'all 0.15s',
              background: balanceMode === mode ? '#FFC107' : 'rgba(255,255,255,0.08)',
              color: balanceMode === mode ? '#000' : 'rgba(255,255,255,0.7)',
            }}>{label}</button>
          ))}
        </div>

        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', opacity: 0.7, fontSize: '0.9rem' }}>
            {balanceMode === 'add' ? 'Amount to Add ($)' : 'Set Balance to ($)'}
          </label>
          <SearchInput
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={balanceOverride}
            onChange={e => setBalanceOverride(e.target.value)}
            autoFocus
            style={{ width: '100%', fontSize: '1.1rem', padding: '0.75rem 1rem' }}
          />

          {/* Result preview */}
          {balanceOverride !== '' && (
            <div style={{
              marginTop: '0.75rem', padding: '0.6rem 0.9rem', borderRadius: '8px',
              background: isClearing ? 'rgba(76,175,80,0.12)' : 'rgba(255,193,7,0.1)',
              border: `1px solid ${isClearing ? 'rgba(76,175,80,0.3)' : 'rgba(255,193,7,0.3)'}`,
              fontSize: '0.85rem',
            }}>
              {balanceMode === 'add' && isAdding && (
                <span style={{ color: '#FFC107' }}>
                  ${currentBalance.toFixed(2)} + ${inputAmt.toFixed(2)} = <strong>${resultBalance.toFixed(2)} due</strong>
                </span>
              )}
              {balanceMode === 'set' && !isClearing && (
                <span style={{ color: '#FFC107' }}>
                  ⚠️ Balance will be set to <strong>${resultBalance.toFixed(2)}</strong> — listings hidden until paid.
                </span>
              )}
              {isClearing && (
                <span style={{ color: '#4CAF50' }}>
                  ✓ Balance cleared — shop listings will be restored automatically.
                </span>
              )}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="modal-button cancel"
            onClick={() => { setShowBalanceModal(false); setBalanceOverride(''); setBalanceMode('add'); }}
            disabled={processingAction}
          >
            Cancel
          </button>
          <button className="modal-button confirm"
            onClick={handleBalanceOverride}
            disabled={balanceOverride === '' || processingAction}
          >
            {processingAction ? <LoadingSpinner /> : isClearing
              ? 'Clear Balance'
              : balanceMode === 'add'
                ? `Add $${inputAmt.toFixed(2)} Due`
                : `Set to $${resultBalance.toFixed(2)}`
            }
          </button>
        </div>
      </ModalContent>
    </ModalOverlay>
  );
})()}

    {/* Resume Account Modal */}
    {showResumeModal && selectedShop && (
      <ModalOverlay onClick={() => !processingAction && setShowResumeModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <h3>
            <Play size={24} />
            Resume Account?
          </h3>
          <p>
            This will resume the account for <strong>{selectedShop.name}</strong>.
            All items and services will become visible in the marketplace again.
          </p>
          <div className="modal-actions">
            <button 
              className="modal-button cancel"
              onClick={() => setShowResumeModal(false)}
              disabled={processingAction}
            >
              Cancel
            </button>
            <button 
              className="modal-button confirm"
              onClick={handleResumeAccount}
              disabled={processingAction}
            >
              {processingAction ? <LoadingSpinner /> : 'Resume Account'}
            </button>
          </div>
        </ModalContent>
      </ModalOverlay>
    )}

    {/* Delete Account Modal */}
    {showDeleteModal && selectedShop && (
      <ModalOverlay onClick={() => !processingAction && setShowDeleteModal(false)}>
        <ModalContent variant="danger" onClick={(e) => e.stopPropagation()}>
          <h3>
            <Trash2 size={24} />
            Delete Account?
          </h3>
          <p>
            This will <strong>permanently delete</strong> the account for <strong>{selectedShop.name}</strong>.
          </p>
          <div style={{ 
            background: 'rgba(244, 67, 54, 0.1)', 
            padding: '1rem', 
            borderRadius: '6px',
            marginBottom: '1rem'
          }}>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#F44336' }}>
              ⚠️ This action cannot be undone. All shop data, items, services, and transactions will be marked as deleted.
            </p>
          </div>
          {selectedShop.analytics?.outstandingBalance > 0 && (
            <div style={{ 
              background: 'rgba(255, 193, 7, 0.1)', 
              padding: '1rem', 
              borderRadius: '6px',
              marginBottom: '1rem',
              border: '1px solid rgba(255, 193, 7, 0.3)'
            }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#FFC107' }}>
                ⚠️ This account has an outstanding balance of ${selectedShop.analytics.outstandingBalance}.
                Deletion will not be allowed until the balance is cleared.
              </p>
            </div>
          )}
          <div className="modal-actions">
            <button 
              className="modal-button cancel"
              onClick={() => setShowDeleteModal(false)}
              disabled={processingAction}
            >
              Cancel
            </button>
            <button 
              className="modal-button confirm"
              onClick={handleDeleteAccount}
              disabled={processingAction}
            >
              {processingAction ? <LoadingSpinner /> : 'Delete Forever'}
            </button>
          </div>
        </ModalContent>
      </ModalOverlay>
    )}

    {/* Request Review Modal */}
    {showRequestsModal && selectedRequest && (
      <ModalOverlay onClick={() => !processingAction && setShowRequestsModal(false)}>
        <ModalContent 
          variant={selectedRequest.requestType === 'delete_request' ? 'danger' : 'warning'}
          onClick={(e) => e.stopPropagation()}
        >
          <h3>
            {selectedRequest.requestType === 'delete_request' && <Trash2 size={24} />}
            {selectedRequest.requestType === 'pause_request' && <Pause size={24} />}
            {selectedRequest.requestType === 'resume_request' && <Play size={24} />}
            Review {selectedRequest.requestType.replace('_request', '').replace('_', ' ')} Request
          </h3>
          
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            padding: '1.5rem', 
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <strong style={{ color: '#800000', display: 'block', marginBottom: '0.5rem' }}>
                  Shop Details
                </strong>
                <p style={{ margin: '0.25rem 0' }}>
                  <strong>Name:</strong> {selectedRequest.shopName}
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <strong>Username:</strong> @{selectedRequest.username}
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <strong>User ID:</strong> 
                  <code style={{ 
                    marginLeft: '0.5rem',
                    background: 'rgba(255,255,255,0.1)', 
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.85rem'
                  }}>
                    {selectedRequest.userId}
                  </code>
                </p>
              </div>
              
              <div>
                <strong style={{ color: '#800000', display: 'block', marginBottom: '0.5rem' }}>
                  Request Information
                </strong>
                <p style={{ margin: '0.25rem 0' }}>
                  <strong>Type:</strong> {selectedRequest.requestType.replace('_', ' ').toUpperCase()}
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <strong>Current Status:</strong> {selectedRequest.currentStatus || 'active'}
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <strong>Requested:</strong> {selectedRequest.createdAt && new Date(selectedRequest.createdAt.toDate()).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          
          {selectedRequest.requestType === 'delete_request' && (
            <div style={{ 
              background: 'rgba(244, 67, 54, 0.1)', 
              border: '1px solid rgba(244, 67, 54, 0.3)',
              padding: '1rem', 
              borderRadius: '6px',
              marginBottom: '1.5rem'
            }}>
              <p style={{ margin: 0, color: '#F44336', fontWeight: 600 }}>
                ⚠️ WARNING: This will permanently delete the account
              </p>
              <ul style={{ 
                margin: '0.5rem 0 0 0', 
                paddingLeft: '1.5rem',
                color: 'rgba(255,255,255,0.8)',
                fontSize: '0.9rem'
              }}>
                <li>All shop data will be marked as deleted</li>
                <li>All items and services will be removed from marketplace</li>
                <li>User will lose access to their account</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>
          )}
          
          {selectedRequest.requestType === 'pause_request' && (
            <div style={{ 
              background: 'rgba(255, 152, 0, 0.1)', 
              border: '1px solid rgba(255, 152, 0, 0.3)',
              padding: '1rem', 
              borderRadius: '6px',
              marginBottom: '1.5rem'
            }}>
              <p style={{ margin: 0, color: '#FF9800', fontWeight: 600 }}>
                ℹ️ This will pause the account temporarily
              </p>
              <ul style={{ 
                margin: '0.5rem 0 0 0', 
                paddingLeft: '1.5rem',
                color: 'rgba(255,255,255,0.8)',
                fontSize: '0.9rem'
              }}>
                <li>Items and services will be hidden from marketplace</li>
                <li>User can still access their dashboard</li>
                <li>Account can be resumed at any time</li>
              </ul>
            </div>
          )}
          
          {selectedRequest.requestType === 'resume_request' && (
            <div style={{ 
              background: 'rgba(76, 175, 80, 0.1)', 
              border: '1px solid rgba(76, 175, 80, 0.3)',
              padding: '1rem', 
              borderRadius: '6px',
              marginBottom: '1.5rem'
            }}>
              <p style={{ margin: 0, color: '#4CAF50', fontWeight: 600 }}>
                ✅ This will resume the paused account
              </p>
              <ul style={{ 
                margin: '0.5rem 0 0 0', 
                paddingLeft: '1.5rem',
                color: 'rgba(255,255,255,0.8)',
                fontSize: '0.9rem'
              }}>
                <li>Items and services will become visible again</li>
                <li>Users can place orders normally</li>
                <li>Account will return to active status</li>
              </ul>
            </div>
          )}
          
          <div className="modal-actions">
            <button 
              className="modal-button cancel"
              onClick={() => {
                setShowRequestsModal(false);
                setSelectedRequest(null);
              }}
              disabled={processingAction}
            >
              Cancel
            </button>
            <button 
              className="modal-button cancel"
              onClick={() => processAccountRequest(selectedRequest.id, 'reject')}
              disabled={processingAction}
              style={{ 
                background: 'rgba(244, 67, 54, 0.2)',
                color: '#F44336',
                border: '1px solid rgba(244, 67, 54, 0.3)'
              }}
            >
              {processingAction ? <LoadingSpinner /> : (
                <>
                  <XCircle size={16} />
                  Reject Request
                </>
              )}
            </button>
            <button 
              className="modal-button confirm"
              onClick={() => processAccountRequest(selectedRequest.id, 'approve')}
              disabled={processingAction}
            >
              {processingAction ? <LoadingSpinner /> : (
                <>
                  <CheckCircle size={16} />
                  Approve & Execute
                </>
              )}
            </button>
          </div>
        </ModalContent>
      </ModalOverlay>
    )}

    <style>
      {`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spinning {
          animation: spin 1s linear infinite;
        }
      `}
    </style>
  </DashboardContainer>
);
};

export default ShopDashboard;